import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { appConfig } from "@/common/config/app.config";
import { resolveWireSecret } from '@bt-common/utils/send-prep';
import { buildEngineSenderClaim, readGatewaySessionId } from './engine-sender-claim';
import ObjectId from "bson-objectid";
import { getSerialized } from "@extension/wa-communication-layer/core/utils/wa-key-serialization";
import { campaignReducerActions, campaignsSelectOneObjectByQuery, DBCampaignThunks, DBUserMessageThunks, userMessageReducerActions } from "@common-reducers/DBServiceThunks";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { shouldDropPhantomRetry } from './wa-retry-liveness';
import { resolveFullDocForSend, needsFullDocForSend } from './send-time-full-doc';
import { resolveRetryCapFailure } from './retry-cap-failure';
import { describeError, retryThunkErrorReason } from './retry-thunk-error-reason';
import { GROUP_GUARD_DECLINE_RE } from '@extension/wa-communication-layer/message/group-media-send-guard';
import { hasDeliveryKey, resolveRetryBudget, serverDocConcluded, ServerLockVerdict } from './retry-server-verdict';
import { MessageStatusSchema } from '@bt-common/schemas/bt/message-status';

import { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
import { getClientType, isWhatsappExtensionContext } from "@common/services/detect-context";
import { isWireError } from "@common/services/post-message-to-whatsapp-functions";
import { waToolSuccess, waToolError, type WaToolResponse, type EngineErrorCode } from "@bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolArgs, WaToolResult } from "@bt-common/interfaces/whatsapp-tool-contracts.interface";
import { MQTTSubscriber } from "@common/noifier-module/MQTTSubscriber";
import { ContentMessageServiceActions } from "@extension/context/content/services/contentMessageServiceActions";
import { prepareMessageForWa } from "@extension/wa-communication-layer/message/message-app-context";
import { isNoLidForUserError } from "@extension/wa-communication-layer/validation/not-on-whatsapp-error";
import { serializeWaMessageKey } from "@bt-common/utils/message-key";
import { promotePendingAck, createMessageAttemptLog, createFailedMessageLog, deleteUserMessageAndCreateLog, getNextCampaignMessage, hasSendAttemptBeenMadeToMessage, checkInitialAttemptLockExists } from "@extension/wa-communication-layer/api/api-calls";
import { registerPendingAck } from "@extension/context/content/pending-ack-registry";
import { markSendTimedOut } from "@extension/context/content/timed-out-send-registry";
import { watchSendAck, ACK_WATCH_HANDOFF_MARGIN_MS } from "@extension/context/content/send-ack-watch";
import { withTimeout } from "@extension/wa-communication-layer/core/utils/wa-error-tracing";
import { boundedStep, markRetryThunkStarted, RETRY_THUNK_ENTERED_LOG } from "./retry-thunk-entry";
import { formatSendTrace, type MediaFailInfo, type SendTraceSnapshot } from "@extension/wa-communication-layer/message/send-stage-trace";
import { awaitPageOutcome, formatLatePageOutcome } from "./late-page-outcome";
import { classifyStoreHit, stripDiagnosticSuffixes, splitDiagnosticSuffixes } from "../../../../bt-common/scheduling/send-protocol";
import { toStoreCheckHit, traceShowsWireHandoff, docRecordsPendingAckCopy } from "./store-check-hit";
import { WaMessage } from "@bt-common/interfaces/wa-store-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { openReminderDialog } from "@common/reducers/RemindersUIReducer";
import { Reminder } from "@common/models/reminder";
import { selectUserId } from "@common/reducers/UserSelectors";
import { triggerRequesthWhatsappTool } from "@common/tools/whatsapp/wa-tools-server-actions";
import { WA_TOOL_COMMANDS } from "../../../../bt-common/interfaces/whatsapp-tools-commands.interface";
// Import directly from client-actions to avoid circular dependency with the wa-tools index barrel
import { WhatsappToolsClientActions } from "@common/tools/whatsapp/wa-tools-client-actions";
import { isScheduledStatus, dispatchScheduledStatus, WHATSAPP_STATUS_TARGET_ID } from "@common/scheduling/status-scheduling";
import { runSendProtocol, type SendFn, type SendLogMetadata } from '@bt-common/scheduling';
import { offerHolds, applySendResultToHolds, describeHoldEvent } from '../services/offer-holds';
import { createExtensionBackendActionsClient } from '@extension/wa-communication-layer/api/backend-actions-client.adapter';
import { findFirstUrlInText, buildLinkPreview, type EngineLinkPreviewResult } from '@extension/context/content/thunks/link-preview-build';
import { storeMatchLogMetadata } from '@extension/wa-communication-layer/message/body-fingerprint';
import { setSendLogMetadata, clearSendLogMetadata, withMqttConnected, type SendLogMetadata as StampedSendLogMetadata } from '@extension/wa-communication-layer/api/send-log-metadata';
import { classifyMessageType } from '@extension/wa-communication-layer/api/message-type-classifier';
import { stampSendEngineFromTrace } from '@extension/wa-communication-layer/api/send-engine-stamp';

import { coldPageSendPacer, waitForPageReady } from './cold-page-gate';
import { notConnectedLogThrottle } from './not-connected-log-throttle';
import { isConnectionClosedError, waConnectionBreaker, waDisconnectedDeferLog } from './wa-connection-breaker';
import { currentReportedExtensionVersion } from '@common/services/reported-extension-version';

/** #462 — a granted-path store lookup older than this gets a durable attempt-log row. */
const STORE_CHECK_SLOW_MS = 15_000;

// Send guard (Promise.race). Raised 50s→180s: fleet send-stage traces show media→group
// sends legitimately take 60-144s (image upload/encrypt + group fan-out) on slow devices /
// networks, so the 50s guard was FALSE-failing sends that then delivered late. 180s gives
// headroom over the 144s observed. Must stay BELOW the IPC ceiling below (190s) so a late
// settlement is still observable within the bus. The `prepareChat` warm-up is capped at a few
// seconds and never fails the send (#409, prepare-chat-warmup.ts), so it can't consume this budget.
const timeoutMs = 180000; // Set your timeout duration (in ms)

// ── Late-settlement telemetry (send-timeout observability) ──────────────────
// `withTimeout` is a Promise.race — when the 50s guard wins it rejects, but the
// underlying send promise is NOT cancelled and its eventual outcome is otherwise
// discarded. Attach a settlement observer (BEFORE the race, so nothing is missed)
// that reports what the orphaned promise finally did — but only once the guard
// has already timed out (`guard.timedOut`), so fast real failures already logged
// on the normal failure path are not double-reported.
//
// `onLate` is injected so the caller can persist this DURABLY via the message-log
// mechanism (createMessageAttemptLog → messagelogs), which makes it queryable for
// post-mortem across every surface — not an ephemeral console line that the CWS
// build's drop_console strips.

/**
 * Coerce a waMessageKey out of the shapes the send paths return: a bare string,
 * a wweb-js MsgKey object (`_serialized`), or a wrapper carrying `id` as either
 * (the poll path returns `{ id: MsgKey }`). Shared by the response-normalization
 * path and the late-settlement classifier — one coercer, one drift surface.
 */
export function coerceWaMessageKey(v: unknown): string {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
        const o = v as { _serialized?: unknown; id?: unknown };
        if (typeof o._serialized === 'string') return o._serialized;
        if (o.id !== undefined && o.id !== v) return coerceWaMessageKey(o.id);
        return '';
    }
    return String(v);
}

/** Raw settlement of the observed promise — classification happens separately. */
export type LateSettlementRaw =
    | { kind: 'fulfilled'; value: unknown; elapsedMs: number }
    | { kind: 'rejected'; error: unknown; elapsedMs: number };

export interface LateSettlementInfo {
    /**
     * resolved     — the send actually completed late (false-negative, slow path);
     * rejected     — the send truly failed late, with the real underlying error;
     * ipc-timeout  — the observed CONTENT→PAGE bus promise itself timed out (it
     *                carries its own 60s ceiling, wa-tools-client-actions), so the
     *                true fate is UNKNOWN from this side — consult the page-side
     *                stage trace embedded in the same row.
     */
    outcome: 'resolved' | 'rejected' | 'ipc-timeout';
    elapsedMs: number;
    waKey?: string;
    error?: string;
}

/**
 * Classify a raw late settlement. Two traps this must handle (review findings):
 * page-handler FAILURES arrive as FULFILLED wire-error envelopes ({success:false})
 * — not rejections — and must not be reported as late deliveries; and the observed
 * promise is the IPC bus call with its own 60s ceiling, whose timeout says nothing
 * about the send's true fate.
 */
export function classifyLateSettlement(raw: LateSettlementRaw): LateSettlementInfo {
    if (raw.kind === 'fulfilled') {
        if (isWireError(raw.value)) {
            const err = (raw.value as { error?: unknown }).error;
            return { outcome: 'rejected', elapsedMs: raw.elapsedMs, error: typeof err === 'string' ? err : String(err ?? 'unknown') };
        }
        return { outcome: 'resolved', elapsedMs: raw.elapsedMs, waKey: coerceWaMessageKey(raw.value) };
    }
    const msg = raw.error instanceof Error ? raw.error.message : String(raw.error);
    if (/postMessageWithResponse.*Timeout|Timeout after \d+ms waiting for cmd/.test(msg)) {
        return { outcome: 'ipc-timeout', elapsedMs: raw.elapsedMs, error: msg };
    }
    return { outcome: 'rejected', elapsedMs: raw.elapsedMs, error: msg };
}

export function observeLateSettlement(
    sendPromise: Promise<unknown>,
    guard: { timedOut: boolean },
    onLate: (raw: LateSettlementRaw) => void,
): void {
    const startedAt = Date.now();
    void sendPromise.then(
        (value) => { if (guard.timedOut) onLate({ kind: 'fulfilled', value, elapsedMs: Date.now() - startedAt }); },
        (error: unknown) => { if (guard.timedOut) onLate({ kind: 'rejected', error, elapsedMs: Date.now() - startedAt }); },
    );
}

/**
 * Pull + format the page-side stage trace for a message, bounded so diagnostics
 * can never stall the caller (a wedged page is the likeliest state when this
 * runs). Shared by the guard-timeout catch and the late-settlement writer so
 * the budget and the format can only drift together.
 */
/** Page failure codes raised before anything is sent (EngineToolError on the
 *  send path): deterministic, so the send fails without a store probe. A bare
 *  `engine_error` (an untyped throw) stays `ambiguous`. */
const PRE_SEND_FAILURE_CODES: readonly string[] = ['invalid_input', 'not_found', 'not_connected', 'refused', 'not_on_whatsapp', 'not_a_group', 'unsupported_command'];
const SEND_TRACE_FETCH_TIMEOUT_MS = 1200;

/** Structured snapshot fetch. #94 needs the trace's `media` detail, not just the
 *  rendered text, so both callers share one bounded fetch. Returns null when the
 *  page is wedged or the bridge times out. */
async function fetchSendTrace(userMessageId: string, startedAt?: number): Promise<SendTraceSnapshot | null> {
    try {
        return await withTimeout(
            startedAt === undefined
                ? ContentMessageServiceActions.getSendTrace(userMessageId)
                : ContentMessageServiceActions.getSendTrace(userMessageId, startedAt),
            'getSendTrace',
            SEND_TRACE_FETCH_TIMEOUT_MS,
        );
    } catch {
        return null;
    }
}

/**
 * #658 — the store hit both gates judge. At ack 0 only, ask the page trace whether the last attempt got
 * past the upload and handed the message to WhatsApp's send queue; if so the hit carries `onWire`, so
 * classifyStoreHit reads it as maybe-sent (recorded pending-ack, never re-sent) instead of unsent.
 * The same holds when the doc already records this copy as sent-pending-ack. Any other ack, or no evidence, leaves the hit as before.
 */
async function storeHitWithWireEvidence(
    result: Parameters<typeof toStoreCheckHit>[0],
    userMessage: { _id: unknown; status?: unknown; waMessageKey?: unknown },
): Promise<ReturnType<typeof toStoreCheckHit>> {
    const base = toStoreCheckHit(result);
    if (!base || base.ack !== 0) return base;
    // Durable evidence first (survives a page reload), then the page trace of the last attempt.
    const onWire = docRecordsPendingAckCopy(userMessage, base.waMessageKey)
        || traceShowsWireHandoff(await fetchSendTrace(String(userMessage._id)));
    return toStoreCheckHit(result, onWire);
}

/** #586: a pre-wire `[resolveChat] …` failure (newsletter no-msgs-model / lookup timeout). The page-side
 *  trace holds `resolveChat:route:*` / `resolveChat:timeout:*`, but nothing pulled it on this path. */
const RESOLVE_CHAT_FAILURE_RE = /\[resolveChat\]/;

async function fetchSendTraceText(userMessageId: string): Promise<string> {
    const snap = await fetchSendTrace(userMessageId);
    return snap ? formatSendTrace(snap) : 'unavailable';
}

/**
 * Pull the `prepareChat` stage duration (ms) out of a formatted send trace,
 * e.g. `... resolveChat=8 prepareChat=1234 poll:sendAndWait=... total=...`.
 * The trailing `*` on a still-open stage is ignored (`\d+` stops at the digits).
 * Returns undefined when the stage isn't present. Never throws.
 */
export function parsePrepareChatMs(trace: string): number | undefined {
    const m = /(?:^|\s)prepareChat=(\d+)/.exec(trace ?? '');
    return m ? Number(m[1]) : undefined;
}

/**
 * #791 media stall observability. The page-side media path parks `@media:asset[bytes=N]` and
 * `@media:qstart[waitMs=N,inflight=K]` on the send trace; read them back so EVERY media send row
 * (success and timeout alike) carries asset size, time queued behind promiseCallSync, and in-flight
 * media sends. Absent tokens yield nothing. Never throws.
 */
export function parseMediaQueueFromTrace(trace: string): {
    mediaSizeBytes?: number; mediaQueueWaitMs?: number; mediaInflightSends?: number;
} {
    const t = trace ?? '';
    const out: { mediaSizeBytes?: number; mediaQueueWaitMs?: number; mediaInflightSends?: number } = {};
    const bytes = /@media:asset\[bytes=(\d+)\]/.exec(t);
    if (bytes) out.mediaSizeBytes = Number(bytes[1]);
    const q = /@media:qstart\[waitMs=(\d+),inflight=(\d+)\]/.exec(t);
    if (q) { out.mediaQueueWaitMs = Number(q[1]); out.mediaInflightSends = Number(q[2]); }
    return out;
}

/**
 * #148 prepareChat-stall instrumentation. The chat-state descriptor built by
 * `describeChatForTrace` rides in the trace as one space-free token, e.g.
 * `@prepareChat:chat[addr=g.us,group=true,hasMeta=true,participants=297,msgs=0,unread=22,pendingLoad=true,recentLoaded=?,readOnly=false]=<ms>`.
 * Parse the store-readiness fields off it so a SUCCESSFUL group send carries the same
 * signal the hang error already does (removing survivorship bias). Each field is keyed
 * on its `[`/`,` delimiter so it can't match a lookalike elsewhere; `?`/absent → undefined.
 * Never throws.
 */
export function parseChatStateFromTrace(trace: string): {
    chatMsgs?: number; chatUnread?: number; chatPendingLoad?: boolean;
    chatRecentLoaded?: boolean; chatReadOnly?: boolean;
} {
    const t = trace ?? '';
    const num = (k: string): number | undefined => {
        const m = new RegExp(`[\\[,]${k}=(\\d+)`).exec(t);
        return m ? Number(m[1]) : undefined;
    };
    const bool = (k: string): boolean | undefined => {
        const m = new RegExp(`[\\[,]${k}=(true|false)`).exec(t);
        return m ? m[1] === 'true' : undefined;
    };
    const out: {
        chatMsgs?: number; chatUnread?: number; chatPendingLoad?: boolean;
        chatRecentLoaded?: boolean; chatReadOnly?: boolean;
    } = {};
    const msgs = num('msgs'); if (msgs !== undefined) out.chatMsgs = msgs;
    const unread = num('unread'); if (unread !== undefined) out.chatUnread = unread;
    const pl = bool('pendingLoad'); if (pl !== undefined) out.chatPendingLoad = pl;
    const rl = bool('recentLoaded'); if (rl !== undefined) out.chatRecentLoaded = rl;
    const ro = bool('readOnly'); if (ro !== undefined) out.chatReadOnly = ro;
    return out;
}

/**
 * #94 media-send observability. Map the media failure detail the page context
 * parked on the trace (`markSendMedia`) onto send-log metadata keys, reusing
 * `parseChatStateFromTrace` for the chat descriptor so the #148 store-readiness
 * keys are filled from the same parser a successful group send uses.
 *
 * Undefined values are omitted rather than written, so a partial capture never
 * clobbers metadata another branch already stashed. Never throws.
 */
export function mediaFailMetadataFromTrace(snap: { media?: MediaFailInfo } | null): SendLogMetadata {
    const media = snap?.media;
    if (!media) return {};

    const out: SendLogMetadata = {};
    const put = <K extends keyof SendLogMetadata>(k: K, v: SendLogMetadata[K]): void => {
        if (v !== undefined) out[k] = v;
    };

    put('mediaFailSite', media.site);
    put('mediaUploadStage', media.uploadStage);
    put('mediaUploadResultKind', media.uploadResultKind);
    put('mediaMimetype', media.mimetype);
    put('mediaSizeBytes', media.sizeBytes);
    // #371: not yet in the bt-common SendLogMetadata type; api-call-helper copies every defined
    // key and the backend allowlists them, so widen locally instead of bumping the pin.
    const wide = out as Record<string, unknown>;
    if (media.failStage !== undefined) wide.mediaFailStage = media.failStage;
    if (media.failCode !== undefined) wide.mediaFailCode = media.failCode;
    if (media.failAck !== undefined) wide.mediaFailAck = media.failAck;
    if (media.resultKeys !== undefined) wide.mediaFailResultKeys = media.resultKeys;
    // BE#1561: same widening, until bt-common's SendLogMetadata carries them.
    if (media.errorName !== undefined) wide.mediaFailErrorName = media.errorName;
    if (media.errorMessage !== undefined) wide.mediaFailErrorMessage = media.errorMessage;

    return { ...out, ...parseChatStateFromTrace(media.chatDescriptor ?? '') };
}

/**
 * #94: pull the page-side media failure detail onto the durable log.
 *
 * MUST be called from BOTH failure paths. A media send fails in two different
 * shapes and they exit the tool at different places:
 *   - the page handler RETURNS an error envelope  → isWireError branch
 *     (`PAGE_HANDLER_THREW`, "... (secret=..., chatId=...)")  ← the common case
 *   - the page handler THROWS                     → catch branch
 *     (`SEND_THREW`, "... (userMessageId=..., chatId=...)")
 *
 * The original wiring only covered the throw path, so in production the
 * structured keys never landed — every failed row carried the ` | media-diag: `
 * string (which rides inside the error text and crosses contexts for free) and
 * none of mediaFailSite/mediaUploadStage/... Verified live on 5.8.75: 4/4 rows
 * had the suffix, 0/4 had the fields.
 *
 * Returns the send-trace suffix so callers can append it; stamping is a side
 * effect. Best-effort — a null snapshot yields {} and changes nothing.
 */
async function stampMediaFailMetadata(userMessageId: string): Promise<string> {
    const snap = await fetchSendTrace(userMessageId);
    const mediaMeta = mediaFailMetadataFromTrace(snap);
    if (Object.keys(mediaMeta).length > 0) {
        setSendLogMetadata(userMessageId, mediaMeta);
    }
    return ` | send-trace: ${snap ? formatSendTrace(snap) : 'unavailable'}`;
}

/**
 * #448: a NON-media send (text/poll/status/group) fails with only WA's flattened error. The
 * page-side trace carries the send-job cause (nack code / class) for every send type, so stamp
 * it; stamps nothing when the trace has none. Never throws, adds no suffix to the error text.
 */
async function stampObservedCauseMetadata(userMessageId: string): Promise<void> {
    try {
        const snap = await fetchSendTrace(userMessageId);
        if (!snap?.media?.failCode) return;
        const meta = mediaFailMetadataFromTrace(snap);
        if (Object.keys(meta).length > 0) setSendLogMetadata(userMessageId, meta);
    } catch {
        // diagnostics only
    }
}

// ── Send-log metadata resolution (#send-log-metadata) ───────────────────────────────────────
// Resolve group/community + message-type + addressing metadata for a send so it can be stamped
// onto the message-log rows (persisted in messagelogs.metadata). ALL resolution is best-effort and
// MUST NEVER break or slow-fail the send — group lookups are wrapped in try/catch + a short timeout.

const GROUP_META_TIMEOUT_MS = 5000;
const SEND_ERROR_REASON_MAX = 300;
// #94: the diagnostic suffixes get their own budget on their own fields. 300 was never a
// considered limit for a TRACE — it was the bound for a short error string that the trace
// happened to be glued onto. The send trace is a structured stage list
// (`type=… resolveQuoted=… resolveChat=… @prepareChat:chat[…] …`); 2000 holds a full one
// with headroom, and it is written only on a FAILED send, so the row-count is the failure
// rate (~1.8k/day), not the send rate.
const SEND_TRACE_MAX = 2000;

/** Classify the recipient JID by suffix. isGroup is ONLY the `@g.us` case. */
function classifyRecipientAddressing(chatId: string): { recipientAddressing: string; isGroup: boolean } {
    if (chatId.endsWith('@g.us')) return { recipientAddressing: 'g.us', isGroup: true };
    if (chatId.endsWith('@lid')) return { recipientAddressing: 'lid', isGroup: false };
    if (chatId.endsWith('@c.us')) return { recipientAddressing: 'c.us', isGroup: false };
    if (chatId.endsWith('@newsletter')) return { recipientAddressing: 'newsletter', isGroup: false };
    if (chatId.endsWith('@broadcast')) return { recipientAddressing: 'broadcast', isGroup: false };
    return { recipientAddressing: 'unknown', isGroup: false };
}

// classifyMessageType moved to a pure, unit-tested module (FE#119): the old
// inline version stamped ~all sends 'poll' (empty poll scaffold) and missed
// MIME media types. See ../../../wa-communication-layer/api/message-type-classifier.

/** Resolve the full send-log metadata for a message. Group lookup only runs for `@g.us`.
 *  The group/community fields need `window.WWebJSBt.getChat`, a PAGE-context global that is
 *  `undefined` in this content-script world — so they are resolved via the `getChatLogMetadata`
 *  page action (was previously read directly in the content world → always returned `{}`).
 *  Best-effort + timeboxed: a failed lookup leaves the group fields undefined, never breaks the send. */
async function resolveSendLogMetadata(chatId: string, userMessage: UserMessage): Promise<SendLogMetadata> {
    const { recipientAddressing, isGroup } = classifyRecipientAddressing(chatId);
    const messageType = classifyMessageType(userMessage);
    const base: SendLogMetadata = { recipientAddressing, isGroup, messageType };
    if (!isGroup) return base;
    let groupMeta: Partial<SendLogMetadata> = {};
    try {
        groupMeta = await withTimeout(
            ContentMessageServiceActions.getChatLogMetadata(chatId),
            'getChatLogMetadata-sendLogMetadata',
            GROUP_META_TIMEOUT_MS,
        ) ?? {};
    } catch {
        // Metadata gathering must never break the send — leave the group fields undefined.
        groupMeta = {};
    }
    return { ...base, ...groupMeta };
}

// ── Account-restriction pre-send gate ──────────────────────────────────────────────────────
// While WhatsApp has an account under a reachout timelock, it refuses sends to recipients who
// are not established contacts — and every refused attempt is precisely the signal that
// deepens the restriction. WA's own client gates this before it will let a user press send
// (`WAWebReachoutTimelockUtils.canSendMsgWhileTimelocked`); we mirror that predicate so a
// scheduled send does not spend the account's remaining goodwill on a message that cannot land.
//
// FAILS OPEN, always. An unreachable probe, an unknown chat, a slow answer — all resolve to
// "send it". The gate exists to stop an ACTIVE restriction getting worse; it must never become
// a new way for a healthy send to fail. On an unrestricted account it is a single cheap
// page round-trip that always answers `blocked: false`.
const RESTRICTION_GATE_TIMEOUT_MS = 3000;

async function evaluateRestrictionGate(chatId: string): Promise<{ blocked: boolean; reason: string } | null> {
    const id = String(chatId ?? '').trim();
    if (!id) return null;
    // Groups, channels and broadcasts are exempt in WhatsApp's own predicate (it only gates
    // ordinary user contacts), so skip the round-trip entirely rather than ask and be told no.
    if (!id.endsWith('@c.us') && !id.endsWith('@lid')) return null;

    try {
        // Dynamic import: WhatsappThunks' module graph reaches app.config, which reads
        // `window.location` at import time and breaks this thunk's own test suite when pulled
        // in statically. Mirrors the dynamic-import convention used elsewhere for the same reason.
        const { waChatSendRiskTool } = await import('@extension-context-content/thunks/WhatsappThunks');
        const result = await withTimeout(
            waChatSendRiskTool({ chatId: id }),
            'waChatSendRiskTool-preSendGate',
            RESTRICTION_GATE_TIMEOUT_MS,
        );
        if (!result || result.success === false) return null;
        const risk = result.data;
        if (risk.status !== 'ok' || !risk.blocked) return null;
        console.warn(
            `[sendMessageThunk] account restricted and ${id} has no trusted-contact token — skipping send`,
        );
        return { blocked: true, reason: risk.reason ?? 'WhatsApp has temporarily restricted this account' };
    } catch (error) {
        // Never let the gate itself break a send.
        console.warn('[sendMessageThunk] restriction gate failed (proceeding with send):', (error as Error)?.message);
        return null;
    }
}

const DISABLE_SEND_MESSAGE = false;

// ── Ack model (sent_pending_ack observability) ─────────────────────────────────────────────
// A returned waMessageKey is NOT proof of delivery. So a send is logged `sent_pending_ack` the
// instant it returns (ack 0 — to WhatsApp but not yet acked = not-sent, stays queued). The status
// "upgrade" to `confirmed` then comes NATURALLY from WhatsApp's own `change:ack` push (handled in
// contentMessageServiceHandlers → promote-pending-ack) — no polling, no timeouts. At send we just
// stash waMessageKey → userMessage._id in the pending-ack registry so that push can find our message.
const SEND_ACK_PENDING = 0;               // ack we report at send time: PENDING (not yet acked)

/** Success-side payload — the diagnostic fields that downstream
 *  consumers (notifier-handlers, sendScheduleMessageThunk) read, plus
 *  the contract-mandated `id`/`chatId` so the backend's `engineResult.id`
 *  read at `messages.service.ts:225` finds the wire-format message id.
 *  (Pre-refactor the thunk emitted only `messageId`, not `id`, so the
 *  v1 `key` field came back empty on chrome direct-send — surfaced by
 *  the strict-typing pass.) */
export type SendMessageThunkData = WaToolResult<typeof WA_TOOL_COMMANDS.sendMessageThunkTool>;
export type SendMessageThunkResult = WaToolResponse<SendMessageThunkData>;

/**
 * Resolve the link preview ON the send-thunk userMessage. Two caller shapes:
 *
 *   1. Caller already crawled the URL and passed `linkPreview` (the crawl
 *      result) — used as-is, no fetch. (The diagnostics SCHEDULE path and the
 *      product editor both pre-resolve so their UI can render the card.)
 *   2. Caller only set the `sendLinkPreview` flag (no crawl result) — the tool
 *      fetches it here with WhatsApp's own crawler (waGetLinkPreviewTool) for
 *      the first URL in the body, then fills `linkPreview` so the page-side
 *      `prepareMessageForWa` attaches the card.
 *
 * Runs in the engine/extension context (where WAWeb lives). No-op when no
 * preview was requested; drops the flag when the body has no URL. Never throws
 * — a preview hiccup must not fail an otherwise-valid send.
 */
async function resolveSendThunkLinkPreview(userMessage: UserMessage): Promise<void> {
    if (!userMessage?.sendLinkPreview) return;

    const existing: any = userMessage.linkPreview;
    if (existing && (existing.url || existing.title || existing.thumbnail)) {
        return; // caller supplied the crawl result — use it verbatim
    }

    const url = findFirstUrlInText(String(userMessage.message ?? ''));
    if (!url) {
        // Flag set but nothing to preview — drop it so we don't attach an empty card.
        userMessage.sendLinkPreview = false;
        userMessage.linkPreview = null;
        return;
    }

    let fetched: EngineLinkPreviewResult | null = null;
    try {
        const result = await WhatsappToolsClientActions.waGetLinkPreviewTool(url);
        fetched = result && !isWireError(result) ? (result as EngineLinkPreviewResult) : null;
    } catch (error) {
        console.warn('[sendMessageThunkTool] link preview fetch failed:', error);
    }

    const resolvedPreview = buildLinkPreview(fetched, url);
    userMessage.sendLinkPreview = true;
    userMessage.linkPreview = resolvedPreview as any;
    console.log('[sendMessageThunkTool] link preview resolved', {
        url: resolvedPreview.url,
        title: resolvedPreview.title,
        hasThumbnail: !!resolvedPreview.thumbnail,
    });
}

/** FE#360 — surfaced verbatim on the usermessage (sendFailureReason) and in the
 *  UI when the at-most-once ledger declines an automatic Status repost. */
const STATUS_NOT_REPOSTED_REASON =
    'Status not re-posted automatically — it may already be live on WhatsApp. Use retry to post it again.';

export async function sendMessageThunkTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.sendMessageThunkTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.sendMessageThunkTool>>> {
    const { userMessage, options } = params;
    const withTyping = options?.withTyping ?? true;
    const typingSeconds = options?.typingSeconds ?? 0;
        const normalizedUserMessage: UserMessage = { ...(userMessage || {}) };
        if (!(normalizedUserMessage as any)._id && (normalizedUserMessage as any).id) {
            (normalizedUserMessage as any)._id = (normalizedUserMessage as any).id;
        }
        if (!(normalizedUserMessage as any)._id) {
            // rev 3.3 §6.6: a direct send with no usermessage doc still gets a
            // REAL 24-hex ObjectId — it is the messageSecret token, and the
            // marked format only fits 24 bytes (the old `diag_send_<ms>_<rand>`
            // was 30 bytes → unmarked legacy secret, invisible to store-check/ack).
            (normalizedUserMessage as any)._id = ObjectId().toHexString();
        }

        // Diagnostics/error context correlates by the message id. The result's
        // `secret` is the wire token the page actually encoded (prepareSend's
        // wireSecret, returned by the page handler) — never recomputed here.
        const idForLog = String(normalizedUserMessage._id);

        if (!isWhatsappExtensionContext()) {
            // Server-mode: forward to backend /actions endpoint. Already
            // returns WaToolResponse — pass through, lifting success.data
            // into the SendMessageThunkData shape.
            // The engine (extension-mode, below) already returns the canonical
            // SendMessageThunkData (chatId / secret / response / waMeInfo).
            // Pass it through unchanged — no re-shaping here.
            const serverResp = await triggerRequesthWhatsappTool<{ userMessage: UserMessage; options?: typeof options }, SendMessageThunkData>(
                WA_TOOL_COMMANDS.sendMessageThunkTool,
                { userMessage: normalizedUserMessage, options },
            );
            return serverResp;
        }

        const chatId = String(normalizedUserMessage?.contactList?.[0]?.id || '').trim();

        // FE#335 — scheduled WhatsApp Status ("Story"). A scheduled message
        // targeting the status@broadcast sentinel posts to Status via the
        // dedicated engine primitive, NOT the chat-send path. One-shot: on a
        // returned messageId we terminalize as delivered (a persisted
        // waMessageKey → isAlreadyDelivered → no redispatch). Status has no
        // per-recipient ack, so it must never enter the ack-gated retry roster.
        if (isScheduledStatus(normalizedUserMessage)) {
            const statusResult = await dispatchScheduledStatus(
                normalizedUserMessage,
                (p) => WhatsappToolsClientActions.createStatusTool(p),
                { allowRepost: !!options?.isSendMessageNow },
            );
            // FE#360 — a ledger refusal means "this engine already ATTEMPTED this
            // Status and the outcome was never observed". That is not a delivery.
            // An earlier revision terminalized it with a synthetic
            // `dedup-<id>` key, which the backend's hasDeliveryKey accepts — so a
            // Status that may never have posted was recorded as delivered, with
            // nothing surfaced to the user. Report the truth instead: it is not
            // sent, we are deliberately not re-posting it, and the retry button
            // (which sets allowRepost) is the way to post it again.
            if (statusResult.alreadyAttempted) {
                return waToolError('refused', `${STATUS_NOT_REPOSTED_REASON} (userMessageId=${idForLog})`);
            }
            if (!statusResult.success) {
                return waToolError('engine_error', `Status post failed: ${statusResult.error} (userMessageId=${idForLog})`);
            }
            // Canonical `_serialized` so the always-enforced isAlreadyDelivered (FE#328) ALSO
            // terminalizes a resend — belt-and-suspenders with the BE status_no_ack_retry guard.
            const statusMsgId = statusResult.messageId;
            if (statusResult.unverifiedId) {
                console.warn(`[sendMessageThunkTool] FE#47 Status posted; WA id not read back, confirming with ${statusMsgId} (userMessageId=${idForLog})`);
            }
            return waToolSuccess<SendMessageThunkData>({
                waMessageKey: serializeWaMessageKey({ fromMe: true, remoteJid: chatId, id: statusMsgId }),
                chatId,
                // The Status wire token: status-operations encodes resolveWireSecret({_id}).
                secret: resolveWireSecret({ _id: normalizedUserMessage._id }),
            });
        }

        const body = String(normalizedUserMessage?.message || '').trim();
        const hasMedia = !!String(normalizedUserMessage?.asset?.url || '').trim();
        const hasPoll = !!normalizedUserMessage?.isPoll || !!normalizedUserMessage?.poll;
        const hasContent = body.length > 0 || hasMedia || hasPoll;
        const replyMessageKey = String(
            normalizedUserMessage?.replyData?._serialized ||
            normalizedUserMessage?.replyData?.id ||
            normalizedUserMessage?.quotedMessageId ||
            ''
        ).trim();
        const isNewsletterChat = chatKindOf(chatId) === 'channel';

        if (!chatId) return waToolError('invalid_input', `chatId is required (userMessageId=${idForLog})`);
        if (!hasContent) return waToolError('invalid_input', `Message content is required (text, media, or poll) (userMessageId=${idForLog}, chatId=${chatId})`);
        if (isNewsletterChat && replyMessageKey) return waToolError('invalid_input', `Reply to newsletter messages is not supported (userMessageId=${idForLog}, chatId=${chatId})`);

        type SendNowConnectionState = {
            isConnected: boolean;
            state?: string | null;
            stream?: string | null;
            hasSynced?: boolean | null;
            error?: string;
        };
        let connection: SendNowConnectionState;
        try {
            const connectionResult = await withTimeout(
                WhatsappToolsClientActions.isWhatsAppConnectedThunkTool(),
                'isWhatsAppConnectedThunkTool',
                timeoutMs
            );
            connection = {
                isConnected: !!connectionResult?.connected,
                state: connectionResult?.state,
                stream: connectionResult?.stream,
                hasSynced: connectionResult?.has_synced,
            };
        } catch (error: any) {
            connection = {
                isConnected: false,
                error: error?.message || String(error),
            };
        }

        if (!connection?.isConnected) {
            return waToolError(
                'not_connected',
                `${connection?.error || 'WhatsApp is not connected'} (userMessageId=${idForLog}, chatId=${chatId}, state=${connection?.state ?? 'unknown'}, stream=${connection?.stream ?? 'unknown'}, hasSynced=${connection?.hasSynced ?? false})`,
            );
        }

        let waMeInfo: any;
        try {
            waMeInfo = await withTimeout(
                WhatsappToolsClientActions.getWaMeInfoTool(),
                'getWaMeInfoTool',
                timeoutMs
            );
        } catch {
            waMeInfo = undefined;
        }

        // Resolve the link preview before sending: use a caller-supplied crawl
        // result as-is, or fetch one via WhatsApp's crawler when only the
        // `sendLinkPreview` flag was set. Runs here (engine context) so the
        // fetch hits WAWeb directly and every send path — direct + scheduler —
        // gets the same behavior.
        await resolveSendThunkLinkPreview(normalizedUserMessage);

        // Humanize text sends: briefly show the "typing…" indicator then clear
        // it before the message goes out, so outgoing text doesn't appear
        // instantly. Duration = built-in 0.4–0.8s random jitter + the caller's
        // `typingSeconds` (default 0). Gated by `withTyping` (default true) and
        // only for messages with a text body (skip pure media/poll sends).
        // Best-effort — wrapped so a presence hiccup can never block or fail
        // the actual send.
        if (withTyping && body.length > 0) {
            try {
                await WhatsappToolsClientActions.waSendStateTypingTool(chatId);
                const typingMs = 400 + Math.floor(Math.random() * 400) + Math.max(0, typingSeconds) * 1000; // 0.4–0.8s + typingSeconds
                await new Promise((resolve) => setTimeout(resolve, typingMs));
                await WhatsappToolsClientActions.waClearStateTool(chatId);
            } catch (typingError) {
                console.warn(`[sendMessageThunkTool] typing simulation failed (non-fatal) (userMessageId=${idForLog}, chatId=${chatId}):`, typingError);
            }
        }

        // Declared outside the try so the catch can flip it after a guard
        // timeout, signalling the late-settlement observer to report.
        // traceStartedAt pins the timed-out attempt's page trace (#503).
        const guard: { timedOut: boolean; traceStartedAt?: number } = { timedOut: false };
        try {
            // Late-settlement telemetry: withTimeout is Promise.race — when the
            // 50s guard wins it rejects, but the underlying send promise is NOT
            // cancelled and its eventual outcome is otherwise discarded. Observe
            // its settlement (attached before the race) so a guard timeout is no
            // longer a dead end. See observeLateSettlement.
            const sendPromise = WhatsappToolsClientActions.sendMessageViaWhatsapp(normalizedUserMessage);
            // Capture only the small values the late writer needs — the closure
            // must not retain the full userMessage (media payloads) while the
            // orphaned promise pends.
            const lateUserMessageId = String(normalizedUserMessage._id);
            const lateChatConnected = connection.isConnected ? 'connected' as const : 'disconnected' as const;
            observeLateSettlement(sendPromise, guard, (raw) => {
                // Persist DURABLY to messagelogs (same mechanism as every other
                // send-log row) so a timed-out send's true fate is recoverable
                // for post-mortem on any surface. Keyed by messageId → joins to
                // the failure row the timeout already wrote. Fire-and-forget.
                // The signal rides logMessage (internal-only). Classification
                // handles the two IPC traps: wire-error envelopes arrive as
                // FULFILLED (→ rejected), and the bus's own 60s ceiling (→
                // ipc-timeout: true fate unknown, consult the embedded trace).
                void (async () => {
                    const info = classifyLateSettlement(raw);
                    const detail = info.outcome === 'resolved'
                        ? `waKey=${info.waKey ?? ''}`
                        : `error=${String(info.error ?? '').slice(0, SEND_ERROR_REASON_MAX)}`;
                    const trace = await fetchSendTraceText(lateUserMessageId);
                    await createMessageAttemptLog(
                        lateUserMessageId,
                        'initial',
                        lateChatConnected, // measured at send time (may be stale by settlement)
                        `Late send settlement after ${timeoutMs}ms timeout — ${info.outcome} (elapsedMs=${info.elapsedMs} ${detail}) trace: ${trace}`,
                    );
                    // #503: the IPC gave up but WhatsApp's send may still be running
                    // page-side. Follow that attempt to its real end (late delivery, or
                    // the nack behind a store ack -1) and log it as a second row.
                    const startedAt = guard.traceStartedAt;
                    if (info.outcome === 'ipc-timeout' && startedAt === undefined) {
                        console.warn(`[late-page-outcome] no pinned trace startedAt, not following page outcome (userMessageId=${lateUserMessageId})`);
                    } else if (info.outcome === 'ipc-timeout' && startedAt !== undefined) {
                        const pageOutcome = await awaitPageOutcome(() => fetchSendTrace(lateUserMessageId, startedAt));
                        await createMessageAttemptLog(
                            lateUserMessageId,
                            'initial',
                            lateChatConnected,
                            formatLatePageOutcome(pageOutcome, timeoutMs),
                        );
                    }
                })().catch(() => { /* best-effort telemetry — never affects the send */ });
            });
            const sendResponse = await withTimeout(
                sendPromise,
                "sendMessageViaWhatsapp",
                timeoutMs
            );

            // Canonical WaSendMessageThunkResult (BE#93): id is the bare
            // wire-format message id. The pre-canonical defensive chain
            // (_serialized / id._serialized / key._serialized / .msg
            // wrapper / .error field) is gone — the page handler emits the
            // schema shape directly. The page wireHandler error envelope
            // surfaces as `success: false`; narrow first.
            if (isWireError(sendResponse)) {
                const err = sendResponse.error;
                const msg = typeof err === 'string' ? err : String(err ?? 'unknown');
                // #94: a media send fails HERE (returned envelope), not via a
                // throw — so the media metadata must be stamped on this path too.
                let mediaTrace = '';
                if (/Media send failed with result/.test(msg)) {
                    mediaTrace = await stampMediaFailMetadata(String(normalizedUserMessage._id));
                } else if (isNoLidForUserError(msg) || GROUP_GUARD_DECLINE_RE.test(msg) || RESOLVE_CHAT_FAILURE_RE.test(msg)) {
                    // FE#179: record WHICH stage threw No-LID (resolveChat vs
                    // the send itself) — the messagelog had no way to tell.
                    // #534: likewise which group-guard branch declined (`@groupGuard:decline[…]`).
                    mediaTrace = ` | send-trace: ${await fetchSendTraceText(String(normalizedUserMessage._id))}`;
                } else {
                    await stampObservedCauseMetadata(String(normalizedUserMessage._id));
                }
                // #892: keep the engine's own errorType. "No LID for user" is WA Web's
                // local lid↔pn mapping miss, not a server "no account" verdict (see
                // not-on-whatsapp-error.ts), so it is NOT relabelled `not_on_whatsapp`.
                return waToolError(sendResponse.errorType ?? 'engine_error', `${msg} (userMessageId=${idForLog}, chatId=${chatId})${mediaTrace}`);
            }
            // The typed send result (plan A.9.6), as the page produced it (the
            // handler-result boundary already mapped it to wire form), plus
            // the send's wire secret token.
            const sent = sendResponse as SendMessageThunkData;
            return waToolSuccess<SendMessageThunkData>({
                waMessageKey: sent.waMessageKey,
                chatId: sent.chatId ?? chatId,
                ...(sent.secret ? { secret: sent.secret } : {}),
                ...(sent.message ? { message: sent.message } : {}),
            });
        } catch (error: any) {
            let traceSuffix = '';
            // #94: a media send that died as ERROR_UNKNOWN parks its structured
            // failure detail on the trace page-side (markSendMedia). Pull it over
            // the same bridge the timeout path uses and stamp it onto the durable
            // messagelog — without this the failure row carries no cause at all.
            // Best-effort: a null snapshot yields {} and the send still fails the
            // way it always did.
            if (/Media send failed with result/.test(error?.message ?? '')) {
                traceSuffix = await stampMediaFailMetadata(String(normalizedUserMessage._id));
            } else if (/Timeout in sendMessageViaWhatsapp/.test(error?.message ?? '')) {
                // Mark that the guard fired so the late-settlement observer
                // reports the orphaned send promise's eventual outcome.
                guard.timedOut = true;
                // #658: WhatsApp may still deliver this copy minutes later. No key came back, so let the
                // durable ack channel forward its SERVER ack to repair the failed row.
                markSendTimedOut(String(normalizedUserMessage._id));
                // Pull the PAGE-side stage trace so the failure row records
                // WHERE the send got stuck (e.g. `@poll:dispatched` present but
                // poll:sendAndWait still open ⇒ the store-add waiter hung).
                // Bounded — diagnostics must never stall the failure path. The
                // suffix feeds metadata.sendErrorReason via the failWith stash;
                // runSendProtocol strips it from the customer-visible
                // sendFailureError/result.error (stripDiagnosticSuffixes).
                const snap = await fetchSendTrace(String(normalizedUserMessage._id));
                guard.traceStartedAt = snap?.startedAt;
                traceSuffix = ` | send-trace: ${snap ? formatSendTrace(snap) : 'unavailable'}`;
                // #791: the timeout row used to lack the survivorship-free signals (prepareChat, chat state,
                // asset size, queue wait) that success rows carry. Stamp them from the same snapshot.
                if (snap) {
                    const failTrace = formatSendTrace(snap);
                    const failMeta: SendLogMetadata = { ...parseMediaQueueFromTrace(failTrace), ...parseChatStateFromTrace(failTrace) };
                    const failPrepareChatMs = parsePrepareChatMs(failTrace);
                    if (failPrepareChatMs !== undefined) failMeta.prepareChatMs = failPrepareChatMs;
                    if (Object.keys(failMeta).length > 0) setSendLogMetadata(String(normalizedUserMessage._id), failMeta);
                }
            } else if (isNoLidForUserError(error?.message) || GROUP_GUARD_DECLINE_RE.test(error?.message ?? '') || RESOLVE_CHAT_FAILURE_RE.test(error?.message ?? '')) {
                // FE#179: same No-LID stage attribution as the envelope branch.
                // #534: a group-guard decline carries its `@groupGuard:decline[…]` token on the trace.
                traceSuffix = ` | send-trace: ${await fetchSendTraceText(String(normalizedUserMessage._id))}`;
            } else {
                await stampObservedCauseMetadata(String(normalizedUserMessage._id));
            }
            // A thrown send (incl. the guard timeout) may have reached WhatsApp:
            // `ambiguous` — the caller probes, never "failed". #892: a thrown
            // "No LID for user" stays `ambiguous` (a transient local mapping miss).
            const rawThrowMessage = error?.message || String(error);
            return waToolError('ambiguous', `${rawThrowMessage} (userMessageId=${normalizedUserMessage._id}, chatId=${chatId})${traceSuffix}`);
        }
}

export const sendScheduleMessageThunk = createAsyncThunk("sendScheduleMessageThunk", async ({ userMessage, isSendMessageNow, userInitiated }: { userMessage: UserMessage; isSendMessageNow?: boolean; /** FE#360 — a PERSON asked for this send (retry / send-now button), so the Status at-most-once ledger must not refuse it. */ userInitiated?: boolean; }, thunkApi) => {
    console.log(`[SEND_MESSAGE_DEBUG] Entry - messageId: ${userMessage._id}, retryCount: ${userMessage.retryCount ?? 0}, status: ${userMessage.status}, isSendMessageNow: ${isSendMessageNow}`);
    const dispatch = thunkApi.dispatch as AppThunkDispatch;
    // Diagnostics and other ad-hoc callers may pass `id` without `_id`.
    // Normalize here so downstream flows always have a message identifier.
    if (!(userMessage as any)._id && (userMessage as any).id) {
        (userMessage as any)._id = (userMessage as any).id;
    }

    if (!isWhatsappExtensionContext()) {
        // Server mode: use backend action via MQTT
        return triggerRequesthWhatsappTool(WA_TOOL_COMMANDS.sendMessageThunkTool, { userMessage, isSendMessageNow });
    }

    // Random failure simulation for testing initial attempt not starting
    // Can be controlled via window.enableInitialAttemptFailures (default: false)
    // This simulates initial attempt not starting (before Redis lock is acquired)
    // Separate from window.enableRandomFailures which controls send failures in wa-communication-layer
    const enableInitialAttemptFailures = (typeof window !== 'undefined' && (window as any).enableInitialAttemptFailures) ?? false;
    const initialAttemptFailureRate = (typeof window !== 'undefined' && (window as any).initialAttemptFailureRate) ?? 0.3; // 30% failure rate by default

    if (enableInitialAttemptFailures && Math.random() < initialAttemptFailureRate) {
        const errorMessage = `[RETRY FLOW SIMULATION]: Initial attempt failure (before lock) - message ${userMessage._id}`;
        console.error(errorMessage);

        // Throw an exception to simulate initial attempt not starting
        // This prevents Redis lock from being acquired, allowing us to test retry validation
        const error = new Error(errorMessage);
        (error as any).type = 'InitialAttemptFailureSimulation';
        (error as any).retryable = true;
        (error as any).timestamp = new Date().toISOString();
        throw error;
    }

    const state = thunkApi.getState() as CommonRootState;
    const currentUserId = selectUserId(state);

    if (userMessage.owner !== currentUserId) {
        console.error(`[sendMessageThunk] Message owner mismatch - message: ${userMessage._id}, owner: ${userMessage.owner}, currentUser: ${currentUserId}`);
        return;
    }

    if (DISABLE_SEND_MESSAGE) {
        console.warn(`[SEND_MESSAGE_DEBUG] DISABLE_SEND_MESSAGE is true - aborting`);
        return;
    }

    // #270: a row from the lean list API carries no thumbnail — fetch the stored preview before sending.
    // Guarded so the common (non-lean) path adds no await / microtask tick.
    if (needsFullDocForSend(userMessage)) {
        const fullDoc = await resolveFullDocForSend(userMessage, async (id) => {
            const docs = await dispatch(DBUserMessageThunks.find({ _id: id, $paginate: false } as any)).unwrap();
            return docs as UserMessage[] | undefined;
        });
        if (fullDoc.action === 'skip') {
            console.warn(`[send-time full doc] dropping send — doc is ${fullDoc.reason}, messageId: ${userMessage._id}`);
            if (fullDoc.reason === 'gone') dispatch(userMessageReducerActions.removeOne(userMessage._id));
            return;
        }
        userMessage = fullDoc.userMessage;
    }

    console.log(`[SEND_MESSAGE_DEBUG] Checking WhatsApp connection - messageId: ${userMessage._id}`);
    const connectionStatus = await withTimeout(ContentMessageServiceActions.isWhatsAppConnectedThunkTool(), 'isWhatsAppConnected', timeoutMs);
    let isWhatsAppConnected = connectionStatus.connected;
    // Engine contract emits nullable engine fields; the downstream send-protocol
    // contract is `string | undefined` only — coerce null→undefined at the boundary.
    let waState: string | undefined = connectionStatus.state ?? undefined;
    let waStream: string | undefined = connectionStatus.stream ?? undefined;
    let waHasSynced: boolean | undefined = connectionStatus.has_synced ?? undefined;

    // #699 — `connected` is computed by the page as state && stream && hasSynced, so a tab that just
    // woke shows up as connected:false with state+stream set and hasSynced:false. Rather than bounce
    // every due message off the not-connected return below, wait (bounded) for the sync to finish.
    // Not ready in time: leave without taking the lock or burning a retry; the scheduler re-offers
    // the message (same contract as the not-connected return).
    if (!isWhatsAppConnected && waState && waStream && waHasSynced === false) {
        coldPageSendPacer.markCold();
        const readiness = await waitForPageReady(
            async () => {
                const st = await withTimeout(ContentMessageServiceActions.isWhatsAppConnectedThunkTool(), 'isWhatsAppConnected', timeoutMs);
                return { connected: !!st.connected, hasSynced: st.has_synced, state: st.state, stream: st.stream };
            },
            { connected: false, hasSynced: false, state: waState, stream: waStream },
        );
        waState = readiness.last.state ?? waState;
        waStream = readiness.last.stream ?? waStream;
        waHasSynced = readiness.last.hasSynced ?? undefined;
        isWhatsAppConnected = readiness.ready;
        if (readiness.ready) {
            console.info(`[sendMessageThunk] #699 hasSynced gate: page ready after ${readiness.waitedMs}ms, messageId: ${userMessage._id}`);
        }
        if (!readiness.ready) {
            console.warn(`[sendMessageThunk] #699 page not synced after ${readiness.waitedMs}ms - send deferred, messageId: ${userMessage._id}`);
            if (userMessage._id) {
                await createMessageAttemptLog(
                    userMessage._id,
                    (userMessage.retryCount ?? 0) === 0 ? 'initial' : 'client-retry',
                    'connected',
                    `Send deferred - WhatsApp page not synced after ${readiness.waitedMs}ms (hasSynced=false) (#699)`,
                    undefined,
                    undefined,
                    getClientType(),
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    waState,
                    waStream,
                    waHasSynced,
                ).catch(() => undefined);
            }
            return;
        }
    }

    const waVersion = await withTimeout(ContentMessageServiceActions.getWaVersion(), 'getWaVersion', timeoutMs);
    const waMeUser = await withTimeout(ContentMessageServiceActions.getWaMeUser(), 'getWaMeUser', timeoutMs);
    console.log(`[SEND_MESSAGE_DEBUG] WhatsApp connection status - messageId: ${userMessage._id}, isConnected: ${isWhatsAppConnected}, waVersion: ${waVersion}, waMeUser: ${waMeUser}, state: ${waState}, stream: ${waStream}, hasSynced: ${waHasSynced}`);
    waConnectionBreaker.observeConnection(isWhatsAppConnected);
    if (!isWhatsAppConnected) {
        console.info("[sendMessageThunk] Whatsapp is not connected");

        // Create log for failed attempt due to WhatsApp not connected (throttled: #1114)
        if (userMessage._id && notConnectedLogThrottle.shouldLog(String(userMessage._id), { state: waState, stream: waStream, hasSynced: waHasSynced })) {
            try {
                const retryCount = userMessage.retryCount ?? 0;
                const attemptText = retryCount === 0 ? "initial send attempt" : `retry attempt #${retryCount}`;
                const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                const clientType = getClientType();
                await createMessageAttemptLog(
                    userMessage._id,
                    retryCount === 0 ? 'initial' : 'client-retry',
                    'disconnected',
                    `WhatsApp not connected - cannot perform ${attemptText}`,
                    undefined,
                    mqttClientId || undefined,
                    clientType,
                    waVersion,
                    waMeUser,
                    undefined, // debugInfo
                    undefined, // error
                    waState,
                    waStream,
                    waHasSynced
                );
            } catch (logError) {
                console.error("[sendMessageThunk] Failed to create log for WhatsApp not connected:", logError);
            }
        }
        return;
    }

    if (!userMessage._id) {
        console.info("[sendMessageThunk] userMessage._id is undefined");
        return;
    }
    notConnectedLogThrottle.clear(String(userMessage._id));

    if (userMessage.deleted) {
        console.info("[sendMessageThunk] userMessage.deleted is true");
        return;
    }

    // #700 — the page says connected but the last send found WhatsApp Web's socket closed: don't dispatch
    // (no lock, no attempt, no retry), unless this message holds the breaker's probe. A person pressing
    // retry / send-now still gets a real attempt.
    const humanSend = !!userInitiated || !!isSendMessageNow;
    if (!humanSend && waConnectionBreaker.decide(String(userMessage._id)) === 'blocked') {
        console.warn(`[sendMessageThunk] #700 WhatsApp Web disconnected - send deferred, messageId: ${userMessage._id}`);
        if (notConnectedLogThrottle.shouldLog(String(userMessage._id), { state: 'breaker-open', stream: waStream, hasSynced: waHasSynced })) await createExtensionBackendActionsClient().createMessageAttemptLog({
            messageId: userMessage._id,
            attemptType: (userMessage.retryCount ?? 0) === 0 ? 'initial' : 'client-retry',
            whatsappStatus: 'disconnected',
            logMessage: waDisconnectedDeferLog('WhatsApp not connected', waConnectionBreaker.openForMs()),
            waState,
            waStream,
            waHasSynced,
        }).catch(() => undefined);
        return;
    }
    // ----- Plan 5f / Task 12: send-protocol cutover ---------------------------
    //
    // The lock pre-flight, lock-denied store-check, pre-attempt log, sendFn
    // invocation, and post-send delete-and-log call are owned by
    // runSendProtocol (bt-common/scheduling). Extension-specific concerns
    // (Redux updates, preemptive recipient validation, recurring-message
    // handling, lastSendAttempt patch, investigation fallback) live in the
    // sendFn body and the post-orchestrator handler below.
    //
    // Deferred concerns NOT migrated in this cut:
    //   * The initialAttemptLockExists-missing recursion the legacy code did
    //     on the allowToSend=false path. Today the orchestrator's store-
    //     adapter handles the store-check, but does not re-trigger an initial
    //     send when the lock is absent. Restore the recursion in a follow-up
    //     if canary shows a regression.
    //   * The "shouldFailMessage" handling on the legacy lock-denied store
    //     check (patch status:'failed' + createFailedMessageLog on a fatal
    //     store error). The orchestrator's adapter swallows store errors.
    //   * Duplicate log writes in sendViaWaAndLogMessageThunk for the
    //     success/failure/exception paths were removed in the plan 5f
    //     Task 12 follow-up — the orchestrator's deleteUserMessageAndCreateLog
    //     is now the only writer for those rows. The investigation-fallback
    //     log writes (distinct diagnostic rows) remain in sendViaWa.
    //   * Legacy outer-catch "No record found for id" cleanup (remove from
    //     Redux store on 404 from has-send-attempt) is preserved below.
    let sendMessageViaWhatsappResponse: any;

    // Resolve group/community + message-type + addressing metadata for this send and stash it by
    // messageId so api-call-helper can stamp it onto every message-log row (messagelogs.metadata).
    // Best-effort — resolveSendLogMetadata never throws. Cleared in the finally below.
    const sendChatId = String(userMessage.contactList?.[0]?.id || '').trim();
    let sendLogMetadata: StampedSendLogMetadata = {};
    try {
        sendLogMetadata = await resolveSendLogMetadata(sendChatId, userMessage);
    } catch (metaErr) {
        console.warn(`[sendMessageThunk] send-log metadata resolution failed (non-fatal):`, metaErr);
    }
    // #503: stamp whether this tab holds a live MQTT connection, per attempt. Never throws.
    sendLogMetadata = withMqttConnected(sendLogMetadata, () => MQTTSubscriber.getInstance().isConnected());
    setSendLogMetadata(userMessage._id, sendLogMetadata);

    try {
        const backendClient = createExtensionBackendActionsClient();
        const mqttClientIdForSend = MQTTSubscriber.getInstance().getCurrentClientId() ?? undefined;
        const clientTypeForSend = getClientType();
        const retryCountForSend = userMessage.retryCount ?? 0;
        const attemptTypeForSend: 'initial' | 'client-retry' | 'scheduler-retry' =
            retryCountForSend === 0 ? 'initial' : 'client-retry';
        const whatsappStatusForSend: 'connected' | 'disconnected' = isWhatsAppConnected ? 'connected' : 'disconnected';

        const sendFn: SendFn = async ({ userMessage: lockedUserMessage }) => {
            // The orchestrator hands us the most-recent userMessage doc from
            // the lock response. Fall back to the caller's view if the lock
            // response did not include it.
            const effectiveUserMessage: UserMessage = (lockedUserMessage as UserMessage) ?? userMessage;

            // Capture the underlying wa-js error onto the stashed metadata so the FAILED log row
            // gets a real `sendErrorReason` (instead of only the generic "failed with error response").
            // Returns the error so callers can `return failWith(e)`.
            //
            // #94: the diagnostic suffixes are split onto their OWN fields rather than sharing this
            // field's budget. Previously the whole composed string was truncated at
            // SEND_ERROR_REASON_MAX, and since ` | send-trace: ` is appended LAST the cap ate it —
            // every one of the 90 instrumented media failures measured 2026-08-02 was clipped at
            // exactly 300 chars, mid-token, cutting off the part that describes what happened AFTER
            // a successful upload. That is the one thing we still need: all 90 reported
            // `uploadStage=UPLOADED uploadResultKind=success`, so the failure is downstream of the
            // upload and the trace tail is where the answer lives.
            //
            // Each part keeps its own bound, so a runaway trace still can't bloat a log row.
            // `code` (A.9.4): a failure that may have reached WhatsApp
            // (timeouts, thrown send errors, a send without a key) is
            // `ambiguous` — runSendProtocol probes the store; deterministic
            // refusals carry their own code.
            const failWith = (error: string, code: EngineErrorCode = 'ambiguous'): { ok: false; error: string; code: EngineErrorCode } => {
                const { base, sendTrace, storeAtFailure } = splitDiagnosticSuffixes(String(error));
                setSendLogMetadata(String(userMessage._id), {
                    sendErrorReason: base.slice(0, SEND_ERROR_REASON_MAX),
                    ...(sendTrace ? { sendTrace: sendTrace.slice(0, SEND_TRACE_MAX) } : {}),
                    ...(storeAtFailure ? { storeAtFailure: storeAtFailure.slice(0, SEND_TRACE_MAX) } : {}),
                });
                return { ok: false as const, error, code };
            };

            // FE#335 — scheduled WhatsApp Status ("Story"). MUST come before the
            // preemptive recipient validation below: `status@broadcast` is a
            // broadcast JID, not a user, so `waValidatePhoneNumberOrChatIdTool`
            // reports it "not on WhatsApp" and the message is terminalized
            // `failed` (the reason is in PERMANENT_ERROR → no retry) before the
            // Status dispatch ever runs. The branch existed only on the tool
            // path (`sendMessageThunkTool`, used by the Chrome gateway); the
            // browser extension's own scheduler drains due messages through
            // THIS thunk, so every extension-scheduled Status was dropped.
            //
            // Placed inside sendFn so the Status still rides the send-protocol
            // lock + dedupe. One-shot: Status has NO per-recipient ack, so we
            // report no `ack` and let the server's `expectsAck` no-ack rule
            // (status@broadcast, like @newsletter) resolve it to `confirmed`
            // rather than parking it in `sent_pending_ack` — a redispatch
            // candidate that would re-post the Status.
            if (isScheduledStatus(effectiveUserMessage)) {
                const statusResult = await dispatchScheduledStatus(
                    effectiveUserMessage,
                    (p) => WhatsappToolsClientActions.createStatusTool(p),
                    // A human pressing retry / send-now overrides the ledger; an
                    // automatic re-dispatch does not.
                    { allowRepost: !!isSendMessageNow || !!userInitiated },
                );
                // FE#360 — the ledger refused this repost because a PRIOR attempt's
                // outcome was never observed. We do not know that it posted, so we
                // must not claim delivery: reporting a synthetic key here would
                // persist a never-verified Status as sent and hide it from the user.
                // Fail honestly with a reason the UI can show; the retry button
                // (allowRepost) is the deliberate way to post it again.
                if (statusResult.alreadyAttempted) {
                    return failWith(STATUS_NOT_REPOSTED_REASON, 'refused');
                }
                if (!statusResult.success) {
                    return failWith(`Status post failed: ${statusResult.error}`, 'engine_error');
                }
                if (statusResult.unverifiedId) {
                    console.warn(`[sendScheduleMessageThunk] FE#47 Status posted; WA id not read back, confirming with ${statusResult.messageId} (userMessageId=${String(userMessage._id)})`);
                }
                // Canonical `_serialized` shape so the always-enforced
                // isAlreadyDelivered (FE#328) ALSO terminalizes a resend.
                return {
                    ok: true as const,
                    waMessageKey: `true_${WHATSAPP_STATUS_TARGET_ID}_${statusResult.messageId}`,
                };
            }

            // The recipient / account-restriction gates run in runSendProtocol
            // (the `validateRecipient` / `sendRisk` adapters below), before
            // this sendFn is called.

            // Prepare + dispatch via the existing engine-specific thunk so
            // its Redux side-effects (waMessageKey updateOne, lastSendAttempt
            // patch, investigation fallback) remain owned by that thunk.
            let preparedWaMessage: WaMessage;
            try {
                preparedWaMessage = await withTimeout(
                    prepareMessageForWa(effectiveUserMessage as any),
                    'prepareMessageForWa',
                    timeoutMs,
                );
            } catch (prepareError: any) {
                return failWith(prepareError?.message || String(prepareError) || 'prepareMessageForWa failed', prepareError?.code === 'invalid_input' ? 'invalid_input' : 'engine_error');
            }

            const dispatched: any = await dispatch(sendViaWaAndLogMessageThunk({
                preparedWaMessage,
                userMessage: effectiveUserMessage,
                attemptType: attemptTypeForSend,
                waVersion,
                waMeUser,
            }));
            const payload: any = dispatched?.payload;
            sendMessageViaWhatsappResponse = payload;

            if (payload?.error) {
                const sendError = String(payload.error);
                // A failure the page raised BEFORE sending (an EngineToolError
                // code) is deterministic; anything else may have reached
                // WhatsApp → `ambiguous` (the protocol probes the store).
                // #892: bare "No LID for user" is NOT a definite not_on_whatsapp (local mapping miss) → ambiguous/retryable.
                const code: EngineErrorCode = PRE_SEND_FAILURE_CODES.includes(payload.code) ? payload.code
                    : 'ambiguous';
                return failWith(sendError, code);
            }
            // wweb-js returns waMessageKey as a MsgKey object whose serialized
            // form is on `_serialized`. Naive String() coercion gives
            // "[object Object]" and corrupts the row written to messageLogs +
            // userMessage.waMessageKey. Coerce defensively via the shared
            // module-scope helper (also used by the late-settlement classifier).
            const waMessageKey =
                coerceWaMessageKey(payload?.waMessageKey) ||
                coerceWaMessageKey(payload?.id) ||
                coerceWaMessageKey(payload?._serialized) ||
                coerceWaMessageKey(payload?.messageId) ||
                '';
            if (!waMessageKey) {
                return failWith('no_wa_message_key_returned');
            }
            // Follow the ack to >= SERVER so the backend can distinguish a real send (confirmed)
            // from one stuck PENDING (sent_pending_ack). Best-effort + bounded; returns early on
            // ack>=1. ack=undefined (read failure) keeps the legacy key→confirmed behavior.
            // Stash waMessageKey → our _id so the natural change:ack push can promote this exact
            // message (synchronous — registered before any async ack callback can run). Report
            // ack=0 (PENDING): backend logs sent_pending_ack; the push upgrades it to confirmed.
            registerPendingAck(waMessageKey, String(effectiveUserMessage._id));

            // #474: bounded, observe-only follow-up of this message's ack (never resends, never throws,
            // not awaited). Tells the server "WA rejected it" (ack -1 + reason) or "still unacked in a
            // live tab" instead of leaving an ambiguous sent_pending_ack.
            const watchedMessageId = String(effectiveUserMessage._id);
            const handoffAt = Date.now() - ACK_WATCH_HANDOFF_MARGIN_MS;
            void watchSendAck(watchedMessageId, waMessageKey, {
                readAck: async () => {
                    const rows = await ContentMessageServiceActions.getWAMessageAckData([{ waMessageKey, messageId: watchedMessageId }]);
                    const entry = rows[0];
                    return !entry || entry.error || entry.ack == null ? null : entry.ack;
                },
                promote: (id, ack) => promotePendingAck(id, ack),
                report: (id, logMessage) => backendClient.createMessageAttemptLog({
                    messageId: id,
                    attemptType: attemptTypeForSend,
                    whatsappStatus: whatsappStatusForSend,
                    logMessage,
                }),
                readReason: async () => (await withTimeout(
                    ContentMessageServiceActions.getSendRejectReason(sendChatId, handoffAt),
                    'getSendRejectReason',
                    SEND_TRACE_FETCH_TIMEOUT_MS,
                )) ?? undefined,
                sleep: (ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
                now: () => Date.now(),
            });

            // #333 slice 6: record which engine carried the send (native / raw / raw-fallback) on
            // the log row. Only when the raw gate could have engaged (flags OFF => skipped, no IPC,
            // no field). Best-effort: must never break a send that already succeeded.
            await stampSendEngineFromTrace({
                // the LOCKED doc, the same one the page gate reads (PageMessageService), not the entry copy
                sendEngine: (effectiveUserMessage as { sendEngine?: unknown }).sendEngine,
                userMessageId: String(userMessage._id),
                fetchTraceText: fetchSendTraceText,
                setMetadata: setSendLogMetadata,
            });

            // #prepchat-cost: on a SUCCESSFUL group send, stamp the measured `prepareChat`
            // stage duration onto the send-log metadata (participants already rode through
            // resolveSendLogMetadata) so the confirmed/sent_pending_ack row carries the real
            // ms-per-participant datapoint. `@g.us` only — DMs pay no extra trace-fetch IPC.
            // Best-effort + bounded (fetchSendTraceText has its own timeout): must never break
            // or materially slow a send that already succeeded.
            if (chatKindOf(sendChatId) === 'group' || (userMessage as { asset?: unknown }).asset) {
                try {
                    const trace = await fetchSendTraceText(String(userMessage._id));
                    // #148: stamp prepareChatMs + the chat-state descriptor (both parsed from the
                    // SAME already-fetched trace — no extra IPC) so a successful group send carries
                    // the store-readiness signal, not just failures.
                    const meta: SendLogMetadata = { ...parseMediaQueueFromTrace(trace) };
                    if (chatKindOf(sendChatId) === 'group') {
                        Object.assign(meta, parseChatStateFromTrace(trace));
                        const prepareChatMs = parsePrepareChatMs(trace);
                        if (prepareChatMs !== undefined) meta.prepareChatMs = prepareChatMs;
                    }
                    if (Object.keys(meta).length > 0) {
                        setSendLogMetadata(String(userMessage._id), meta);
                    }
                } catch (traceErr) {
                    console.warn('[sendMessageThunk] prepareChat metadata stamping failed (non-fatal):', traceErr);
                }
            }

            return { ok: true as const, waMessageKey, ack: SEND_ACK_PENDING };
        };

        // #699 — until one send has succeeded on this page, pace the burst: one at a time, leased so a
        // wedged holder frees the slot, and a long wait defers (no lock taken) instead of piling on.
        const coldSlot = await coldPageSendPacer.acquire();
        if (coldSlot && !coldPageSendPacer.isWarm()) {
            console.info(`[sendMessageThunk] #699 cold-page pacer: slot acquired after ${coldSlot.waitedMs}ms, messageId: ${userMessage._id}`);
        }
        if (!coldSlot) {
            console.warn(`[sendMessageThunk] #699 cold-page pacing wait exceeded - send deferred, messageId: ${userMessage._id}`);
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType: attemptTypeForSend,
                whatsappStatus: whatsappStatusForSend,
                logMessage: 'Send deferred - cold-page pacing queue wait exceeded (#699)',
            }).catch(() => undefined);
            return;
        }
        const result = await runSendProtocol(
            {
                userMessage: {
                    _id: userMessage._id,
                    contactList: userMessage.contactList as any,
                    retryCount: userMessage.retryCount,
                    dueDate: userMessage.dueDate as any,
                    deleted: userMessage.deleted,
                    // #621: runSendProtocol's min-engine-version gate reads this off the message; the field is
                    // not in the UserMessage schema type, and this literal is an explicit pick, so it was dropped.
                    minEngineVersion: (userMessage as { minEngineVersion?: string }).minEngineVersion,
                },
                attemptType: attemptTypeForSend,
                retryAttemptNumber: retryCountForSend,
                isSendMessageNow: isSendMessageNow ?? false,
                whatsappStatus: whatsappStatusForSend,
                waState,
                waStream,
                waHasSynced,
            },
            {
                backendClient,
                sendFn,
                // The pre-send gates (plan A.9.6), run by runSendProtocol before
                // sendFn: a recipient not on WhatsApp, or a send the account
                // restriction would refuse, fails the attempt with its code. A
                // gate-service error never blocks the send. A Status post
                // (status@broadcast) is not a user — the recipient gate skips it.
                // Only the engine's definite answer fails the send (QA 3b E8):
                // `not_on_whatsapp`. A wire error, no answer (IPC timeout) or a
                // group/newsletter not in the store throws, and the protocol
                // lets the send proceed.
                validateRecipient: async (chatId) => {
                    if (chatKindOf(chatId) === 'status') return 'ok';
                    const answer = await WhatsappToolsClientActions.waValidatePhoneNumberOrChatIdTool(chatId, { useForceUsync: false });
                    if (!answer || isWireError(answer)) throw new Error(`recipient check unavailable (${answer ? answer.error : 'no answer'})`);
                    if (answer.valid) return 'ok';
                    if (answer.errorCode === 'not_on_whatsapp') return 'not_on_whatsapp';
                    throw new Error(`recipient check inconclusive (${answer.errorCode ?? 'no code'})`);
                },
                sendRisk: async (chatId) => (await evaluateRestrictionGate(chatId)) ?? { blocked: false, reason: '' },
                store: {
                    findUserMessageKey: async ({ chatId, userMessageId }) => {
                        // #462 — the granted path runs this lookup BEFORE the "Preparing … attempt" row, with no
                        // timeout. A lookup that hangs or errors leaves a consumed slot and no log at all (51
                        // video-to-group sends hit the retry cap with a grant and zero attempt rows). Write a
                        // durable row if it is still pending after STORE_CHECK_SLOW_MS or fails. Observability only.
                        const startedAt = Date.now();
                        const slowTimer = setTimeout(() => {
                            void backendClient.createMessageAttemptLog({
                                messageId: userMessage._id,
                                attemptType: attemptTypeForSend,
                                whatsappStatus: whatsappStatusForSend,
                                logMessage: `Store check still pending after ${STORE_CHECK_SLOW_MS}ms (#462)`,
                            }).catch(() => undefined);
                        }, STORE_CHECK_SLOW_MS);
                        try {
                            const investigationDueDate = userMessage.dueDate
                                ? new Date(new Date(userMessage.dueDate).getTime() - 20 * 60 * 1000)
                                : new Date(Date.now() - 20 * 60 * 1000);
                            const r = await ContentMessageServiceActions.getUnloggedWaMessageKeyIfExistInStore(
                                chatId,
                                userMessageId,
                                investigationDueDate,
                            );
                            if (!r || isWireError(r)) return null;
                            const hit = await storeHitWithWireEvidence(r, userMessage);
                            if (!hit) return null;
                            // Carry the matched Store.Msg's ack through so runSendProtocol can tell a
                            // real, server-acked copy (>= 1) from a never-delivered outbox entry
                            // (PENDING 0 / ERROR -1). Default 0 when the lookup didn't surface an ack.
                            // #458: record WHAT matched (id shape, t, fromMe, and whether its text equalled ours, compared in memory) so a key
                            // bound by the store-check can be audited against the real send.
                            // Only a hit the protocol will BIND (ack >= 1): a PENDING/ERROR copy is treated as
                            // unsent and the real send follows, whose log rows must not inherit its evidence.
                            if (hit.ack >= 1) {
                                setSendLogMetadata(String(userMessage._id), {
                                    storeCheckMatched: true,
                                    storeCheckAck: hit.ack,
                                    storeCheckMethod: r.debugInfo?.searchMethod,
                                    ...(await storeMatchLogMetadata(r.match, String(userMessage._id), userMessage.message)),
                                });
                            }
                            return hit;
                        } catch (storeErr) {
                            console.error('[sendMessageThunk] store-adapter findUserMessageKey error:', storeErr);
                            void backendClient.createMessageAttemptLog({
                                messageId: userMessage._id,
                                attemptType: attemptTypeForSend,
                                whatsappStatus: whatsappStatusForSend,
                                logMessage: `Store check failed after ${Date.now() - startedAt}ms (#462)`,
                                error: storeErr instanceof Error ? storeErr.message : String(storeErr),
                            }).catch(() => undefined);
                            return null;
                        } finally {
                            clearTimeout(slowTimer);
                        }
                    },
                },
                engineContext: {
                    clientType: clientTypeForSend,
                    mqttClientId: mqttClientIdForSend,
                    waVersion,
                    waMeUser,
                    // Build version → threaded into the server's `[send-slot]` observability marker so
                    // old-vs-new endpoint usage is attributable by version during the Phase-2 drain.
                    extensionVersion: currentReportedExtensionVersion(),
                    // Group/community + message-type + addressing metadata — carried alongside
                    // extensionVersion (persisted per-row via api-call-helper stamping).
                    ...sendLogMetadata,
                    // #922 S0b: which engine instance / number is claiming the slot (§2.4).
                    ...buildEngineSenderClaim({ gatewaySessionId: readGatewaySessionId(), mqttClientId: mqttClientIdForSend, waMeUser }),
                },
                log: (level, msg) => {
                    if (level === 'warn') console.warn(`[send-protocol] ${msg}`);
                    else console.log(`[send-protocol] [${level}] ${msg}`);
                },
            },
        ).finally(coldSlot.release);
        if (result.status === 'sent' || result.status === 'skipped_already_sent') coldPageSendPacer.markWarm();
        const connectionClosed = result.status === 'failed' && isConnectionClosedError(result.error);
        if (connectionClosed && !waConnectionBreaker.isOpen()) {
            console.warn(`[sendMessageThunk] #700 send hit a closed WhatsApp Web connection - pausing dispatch, messageId: ${userMessage._id}`);
        }
        waConnectionBreaker.recordSendOutcome(String(userMessage._id), {
            connectionClosed,
            settled: result.status === 'sent' || result.status === 'skipped_already_sent' || result.status === 'failed',
        });

        // ---- Post-orchestrator Redux side-effects --------------------------
        if (result.status === 'sent' || result.status === 'skipped_already_sent') {
            if (result.waMessageKey) {
                dispatch(userMessageReducerActions.updateOne({
                    id: userMessage._id,
                    changes: { waMessageKey: result.waMessageKey },
                }));
            }
            const lr: any = result.logResponse;
            if (lr?.isRecurringScheduled && lr?.newMessage) {
                dispatch(userMessageReducerActions.addOne(lr.newMessage));
            }
            if (lr?.message) {
                dispatch(userMessageReducerActions.updateOne({
                    id: userMessage._id,
                    changes: lr.message,
                }));
            }
            if (!sendMessageViaWhatsappResponse) {
                sendMessageViaWhatsappResponse = { waMessageKey: result.waMessageKey };
            }
        } else if (result.status === 'failed') {
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: userMessage._id,
                    sendFailureError: result.error,
                },
            }));
            if (!sendMessageViaWhatsappResponse) {
                sendMessageViaWhatsappResponse = { error: result.error };
            }
        }
        // 'aborted' and 'lock_denied' statuses: no Redux mutation here.

        // FE#447 server verdicts + the #409 hang hold (see applySendResultToHolds).
        const holdEvent = applySendResultToHolds(offerHolds, userMessage, result);
        if (holdEvent) {
            // H16 (#534): a long hold / drop suppresses the message with no other trace. One row per event
            // (short holds return null). Best-effort observability — never blocks or fails the send.
            void backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType: attemptTypeForSend,
                whatsappStatus: whatsappStatusForSend,
                logMessage: describeHoldEvent(holdEvent),
            }).catch(() => undefined);
        }

        // lastSendAttempt: set when the sendFn was actually invoked. firstSendAttempt: stamped
        // ONCE, on the first such attempt (never overwritten) — bounds re-dispatch of an
        // already-sent-but-stuck message without dropping a not-yet-sent (lagging) one (#98).
        if (result.status === 'sent' || result.status === 'failed') {
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: userMessage._id,
                    lastSendAttempt: new Date(),
                    ...(userMessage.firstSendAttempt ? {} : { firstSendAttempt: new Date() }),
                },
            }));
        }

        // Campaign find-on-miss — preserved from legacy code.
        const campaignIdToCheck = userMessage.campaignId;
        if (campaignIdToCheck) {
            const campaign = state.DBCampaignReducer.entities[campaignIdToCheck];
            if (!campaign) {
                console.warn(`[sendMessageThunk] Campaign ${campaignIdToCheck} not found, calling find on the campaign service`);
                dispatch(DBCampaignThunks.find({}));
            }
        }
    } catch (error) {
        const errAny = error as { response?: { data?: { message?: string } } } | null | undefined;
        if (errAny?.response?.data?.message?.includes("No record found for id")) {
            console.warn(`[sendMessageThunk] Message ${userMessage._id} not found, removing it from the store`);
            dispatch(userMessageReducerActions.removeOne(userMessage._id));
        } else {
            console.error(`Error in sendMessageThunk:`, error);
            if (userMessage._id) {
                try {
                    const whatsappStatus = isWhatsAppConnected ? 'connected' : 'disconnected';
                    const retryCount = userMessage.retryCount ?? 0;
                    const attemptText = retryCount === 0 ? "initial send" : `retry #${retryCount}`;
                    const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                    const clientType = getClientType();
                    await createMessageAttemptLog(
                        userMessage._id,
                        retryCount === 0 ? 'initial' : 'client-retry',
                        whatsappStatus,
                        `Unexpected error during ${attemptText}`,
                        undefined,
                        mqttClientId || undefined,
                        clientType,
                        waVersion,
                        waMeUser,
                        undefined,
                        error,
                        waState,
                        waStream,
                        waHasSynced
                    );
                } catch (logError) {
                    console.error("[sendMessageThunk] Failed to create log for unexpected error:", logError);
                }
            }
        }
    } finally {
        // Drop the per-message send-log metadata once every log row for this send has been written.
        clearSendLogMetadata(userMessage._id);
    }

    return sendMessageViaWhatsappResponse;
});


// Retry send message thunk
export const retrySendMessageThunk = createAsyncThunk("retrySendMessageThunk", async ({ userMessage, retryTrigger }: { userMessage: UserMessage; retryTrigger?: 'manual' | 'mqtt' | 'fcm' }, thunkApi) => {
    console.log(`[retry send] retrySendMessageThunk ENTRY - messageId: ${userMessage._id}, retryTrigger: ${retryTrigger}, currentRetryCount: ${userMessage.retryCount ?? 0}, status: ${userMessage.status}`);
    console.log(`[MANUAL_RETRY_DEBUG] Entry - messageId: ${userMessage._id}, retryTrigger: ${retryTrigger}, currentRetryCount: ${userMessage.retryCount ?? 0}, status: ${userMessage.status}`);

    // #503: the store-check stashes nack evidence in the per-message holder for the cap-exhaustion
    // failed log. This try/finally (body deliberately not re-indented, to keep the diff reviewable)
    // clears it on EVERY exit, so a stale errorCode can never stamp a later attempt's logs.
    try {
    // #547: signal the MQTT handler (which acks only once we have started) BEFORE any await.
    markRetryThunkStarted(userMessage._id);
    if (DISABLE_SEND_MESSAGE) {
        console.warn("[retry send] DISABLE_SEND_MESSAGE is true - aborting");
        console.warn("[MANUAL_RETRY_DEBUG] DISABLE_SEND_MESSAGE is true - aborting");
        return;
    }

    const dispatch = thunkApi.dispatch;
    const state = thunkApi.getState() as CommonRootState;
    const settings = state?.MainReducer?.settings;

    // Shared bt-common backend-actions client. All log/lock writes route
    // through this instead of the legacy ad-hoc helpers in api-calls.
    const backendClient = createExtensionBackendActionsClient();

    // #547: FIRST client row, written before every other await. Without it a stall in the pre-log
    // awaits left the server with an ack and zero client rows. The row text deliberately does not
    // match BE SEND_ATTEMPT_LOG_RE, so it is evidence of entry, never counted as a send attempt.
    // Bounded + swallowed: the log must never be the thing that stalls or fails the send.
    try {
        await boundedStep(backendClient.createMessageAttemptLog({
            messageId: userMessage._id,
            attemptType: (userMessage.retryCount ?? 0) === 0 ? 'initial' : 'client-retry',
            whatsappStatus: 'unknown',
            logMessage: RETRY_THUNK_ENTERED_LOG(retryTrigger),
            retryTrigger,
        }), 'thunk-entered-log');
    } catch (entryLogError) {
        console.warn(`[retry send] thunk-entered log failed/timed out (non-fatal), messageId: ${userMessage._id}`, entryLogError);
    }

    // FE#238: drop PHANTOM retries — a retry whose usermessages doc no longer
    // exists (0 docs) or is deleted. A recurring edit → delete+recreate (FE#151),
    // or an MQTT/FCM retry carrying a stale snapshot, leaves an entry pointing at
    // a now-gone messageId. Retrying it only produces `before-wa-send` rows that
    // can never confirm and makes "resend" appear to do nothing (it recurses into
    // sendScheduleMessageThunk, which silently returns on a gone/deleted doc).
    //
    // The incoming `.deleted` flag alone is not enough: a stale snapshot doesn't
    // carry it. The local store can't prove ABSENCE either — it's a paginated
    // subset, so a legit not-yet-loaded message would be wrongly dropped. So we
    // ask the BACKEND whether a live doc still exists, and FAIL OPEN if that check
    // is unavailable (a transient error must never drop a real retry).
    let retryExistenceDocs: Array<{ deleted?: boolean }> | null = null;
    try {
        // #547: bounded with a named reason (fails open below, but loudly) — this await had no timeout.
        retryExistenceDocs = await boundedStep(dispatch(
            DBUserMessageThunks.find({ _id: userMessage._id, $paginate: false } as any)
        ).unwrap(), 'existence-check') as Array<{ deleted?: boolean }>;
    } catch (existenceError) {
        console.warn(`[retry send] existence check failed — failing open (FE#238), messageId: ${userMessage._id}`, existenceError);
        retryExistenceDocs = null;
    }
    if (shouldDropPhantomRetry({ incomingDeleted: userMessage.deleted, backendDocs: retryExistenceDocs })) {
        console.warn(`[retry send] phantom retry dropped (FE#238) — deleted/gone doc, messageId: ${userMessage._id}`);
        dispatch(userMessageReducerActions.removeOne(userMessage._id));
        return;
    }

    console.log(`[retry send] Settings loaded - isSendMessageRetryEnabled: ${settings?.isSendMessageRetryEnabled}, messageMaxRetries: ${settings?.messageMaxRetries}`);
    console.log(`[MANUAL_RETRY_DEBUG] Settings - isSendMessageRetryEnabled: ${settings?.isSendMessageRetryEnabled}, messageMaxRetries: ${settings?.messageMaxRetries}`);

    // Check if the message is too old and not part of a campaign
    const now = new Date();
    const dueDate = new Date(userMessage.dueDate ?? now);
    const thirtyMinutesInMillis = 30 * 60 * 1000;
    const timeSinceDue = now.getTime() - dueDate.getTime();

    // Get WhatsApp connection status for logging (reused below)
    // #547: bounded — unbounded here, a hung engine probe stalled the thunk before its first attempt row.
    const connectionStatus = await boundedStep(ContentMessageServiceActions.isWhatsAppConnectedThunkTool(), 'isWhatsAppConnected');
    const { connected: isWhatsAppConnected } = connectionStatus;
    // Engine contract emits nullable engine fields; coerce null→undefined at the boundary.
    const waState: string | undefined = connectionStatus.state ?? undefined;
    const waStream: string | undefined = connectionStatus.stream ?? undefined;
    const waHasSynced: boolean | undefined = connectionStatus.has_synced ?? undefined;
    const waVersion = await withTimeout(ContentMessageServiceActions.getWaVersion(), 'getWaVersion', timeoutMs);
    const waMeUser = await withTimeout(ContentMessageServiceActions.getWaMeUser(), 'getWaMeUser', timeoutMs);
    const whatsappStatus = isWhatsAppConnected ? 'connected' : 'disconnected';
    const attemptType: 'initial' | 'client-retry' | 'scheduler-retry' =
        userMessage.retryCount === 0 ? 'initial' : 'client-retry';

    // Log that a retry attempt is being initiated (message was due to be retried)
    // Note: retryCount hasn't been incremented yet, so we don't specify the attempt number here
    try {
        let retryLogMessage: string;
        if (retryTrigger === 'manual') {
            retryLogMessage = `Retry initiated via manual trigger`;
        } else if (retryTrigger === 'mqtt') {
            retryLogMessage = `Retry triggered via MQTT`;
        } else if (retryTrigger === 'fcm') {
            retryLogMessage = `Retry triggered via FCM notification`;
        } else {
            retryLogMessage = `Retry initiated`;
        }
        console.log(`[MANUAL_RETRY_DEBUG] Creating retry initiation log - messageId: ${userMessage._id}, logMessage: ${retryLogMessage}`);
        const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
        const clientType = getClientType();
        await backendClient.createMessageAttemptLog({
            messageId: userMessage._id,
            attemptType,
            whatsappStatus,
            logMessage: retryLogMessage,
            retryTrigger,
            mqttClientId: mqttClientId || undefined,
            clientType,
            waVersion,
            waMeUser,
            waState,
            waStream,
            waHasSynced,
        });
        console.log(`[MANUAL_RETRY_DEBUG] Retry initiation log created successfully`);
    } catch (logError) {
        console.error(`[MANUAL_RETRY_DEBUG] Failed to create pre-retry attempt log:`, logError);
    }

    console.log(`[retry send] Checking expiration - campaignId: ${userMessage.campaignId}, timeSinceDue: ${Math.round(timeSinceDue / 60000)} minutes, threshold: 30 minutes, isManualRetry: ${retryTrigger === 'manual'}`);
    console.log(`[MANUAL_RETRY_DEBUG] Checking expiration - campaignId: ${userMessage.campaignId}, timeSinceDue: ${Math.round(timeSinceDue / 60000)} minutes, threshold: 30 minutes`);

    // Skip 30-minute expiration check for manual retries - user explicitly wants to send
    if (!userMessage.campaignId && timeSinceDue > thirtyMinutesInMillis && retryTrigger !== 'manual') {
        console.warn(`[retry send] Message EXPIRED - messageId: ${userMessage._id} is older than 30 minutes (automatic retry). Returning early.`);
        console.warn(`[MANUAL_RETRY_DEBUG] Message expired - messageId: ${userMessage._id} is older than 30 minutes and not part of a campaign. Setting status to "failed".`);

        // Create failed log indicating the sending window has passed
        try {
            const retryCount = userMessage.retryCount ?? 0;
            const attemptText = retryCount === 0 ? "initial send" : `retry #${retryCount}`;
            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage: `Message expired - ${attemptText} window passed`,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
            });
        } catch (logError) {
            console.error("[retrySendMessageThunk] Failed to create expired message log:", logError);
        }

        // Now patch the message status to failed
        dispatch(DBUserMessageThunks.patch({
            entity: {
                _id: userMessage._id,
                status: "failed"
            }
        }));
        return;
    }

    // Log if manual retry bypasses expiration check
    if (!userMessage.campaignId && timeSinceDue > thirtyMinutesInMillis && retryTrigger === 'manual') {
        console.log(`[retry send] Message is ${Math.round(timeSinceDue / 60000)} minutes old but MANUAL retry - bypassing expiration check`);
    }

    // Check if message was already sent before proceeding with retry
    // This is the FIRST gate - if message was sent, we stop here before any other checks
    console.log(`[retry send] GATE 1: Checking if message already sent in store`);
    // #408 — the ack of this message's copy in WhatsApp's store, kept for the retry-cap failure text.
    let lastStoreCheckAck: number | undefined;
    try {
        console.log(`[MANUAL_RETRY_DEBUG] Checking if message already sent in store - messageId: ${userMessage._id}`);
        const [contact] = userMessage.contactList ?? [];

        // Simulation for testing technical error logging
        if (typeof window !== 'undefined' && (window as any).simulateStoreCheckError) {
            throw new Error("[SIMULATION]: Failed to check store - technical error (testing logging)");
        }

        const result = await withTimeout(ContentMessageServiceActions.getUnloggedWaMessageKeyIfExistInStore(
            contact?.id ?? "",
            userMessage._id,
            new Date(new Date(userMessage.dueDate ?? Date.now()).getTime() - 20 * 60 * 1000) // 20 minutes before the due date
        ), 'getUnloggedWaMessageKeyIfExistInStore', timeoutMs);

        const ok = result && !isWireError(result) ? result : undefined;
        const waMessageKey = ok?.waMessageKey || null;
        const debugInfo = ok?.debugInfo;

        // #94 ACK GATE. A stored copy is not proof of delivery. wa-js Message.ack:
        // -1 ERROR, 0 PENDING (never reached the server), 1 SERVER, 2 DELIVERED, 3 READ.
        // A media send that FAILS still leaves a local outbox model carrying our
        // userMessageId tag, so the Tier-1 store search matches it and this gate used to
        // conclude "already sent" — skipping the retry and marking the message SENT via
        // deleteUserMessageAndCreateLog. Measured 2026-08-01: 271 messages/day across 15
        // owners had a `failed` log and were then confirmed by this path.
        //
        // `runSendProtocol` was ack-gated for exactly this reason (#98 "maylin masking");
        // that fix covered the send protocol's own store-checks and never reached here —
        // the one gate that decides whether a FAILED message gets another attempt.
        //
        // #590 H13 — the verdict is bt-common's classifyStoreHit on the same hit the send protocol's
        // store adapter builds (toStoreCheckHit), so GATE 1 and GATE 2 cannot disagree: a Status post
        // is proven by its tag hit (it has no ack), a maybe-sent copy is recorded instead of re-sent,
        // and an absent ack reads as PENDING. GATE 1 used to keep its own rule (absent ack ⇒ sent).
        const hit = await storeHitWithWireEvidence(ok, userMessage);
        const verdict = hit ? classifyStoreHit(hit, contact?.id) : null;
        const storeAck = hit?.ack;
        if (hit) lastStoreCheckAck = hit.ack;
        const alreadySent = hit !== null && (verdict === 'delivered' || verdict === 'maybe-sent');

        // Always log the result with debug info, whether found or not
        const retryCount = userMessage.retryCount ?? 0;
        const attemptText = retryCount === 0 ? "initial send" : `retry #${retryCount}`;

        // `totalMessagesFetched` is 0 on a Tier-1 (in-memory) hit because nothing was
        // FETCHED — it is not "checked nothing". Report the store search count too, so
        // the log stops reading as though the lookup did no work.
        const debugInfoSummary = debugInfo
            ? ` (fetched ${debugInfo.totalMessagesFetched}, store ${debugInfo.globalStoreSearched ?? '?'}, ack ${storeAck ?? 'n/a'})`
            : '';

        /** Store-check evidence, stamped on every branch so the gate is measurable. */
        const storeCheckMetadata = {
            storeCheckAck: storeAck,
            storeCheckMethod: debugInfo?.searchMethod,
            storeCheckSearched: debugInfo?.globalStoreSearched,
            storeCheckFetched: debugInfo?.totalMessagesFetched,
            storeCheckMatched: !!waMessageKey,
            ...(await storeMatchLogMetadata(ok?.match, String(userMessage._id), userMessage.message)),
        };

        if (verdict === 'unsent') {
            // #503: the cap-exhaustion failed log is written outside this scope, so hand it the
            // nack evidence through the per-message holder (api-call-helper stamps it on the body).
            const { storeCheckErrorCode, storeCheckIsSendFailure } = storeCheckMetadata;
            setSendLogMetadata(String(userMessage._id), { storeCheckAck: storeAck, storeCheckErrorCode, storeCheckIsSendFailure });
            // Matched, but the copy never reached WhatsApp's server. Fall through to the
            // retry rather than reporting a failed message as delivered.
            console.log(`[retry send] GATE 1: store hit at ack=${storeAck} (<1) — NOT sent, proceeding with retry`);
        }

        if (alreadySent) {
            // Message was already sent - log and exit early
            console.log(`[retry send] GATE 1 FAILED: Message already sent (found in store with waMessageKey: ${waMessageKey}). Returning early.`);
            console.log(`[MANUAL_RETRY_DEBUG] Message already sent (found in store) - stopping retry - messageId: ${userMessage._id}`);
            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();
            const logMessage = verdict === 'maybe-sent'
                ? `Message maybe sent (found in store, ack not seen) - recorded pending-ack, ${attemptText} skipped${debugInfoSummary}`
                : `Message already sent (found in store) - ${attemptText} skipped${debugInfoSummary}`;
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
                ...storeCheckMetadata,
            });

            // Patch the message status to "sent" via direct backend write.
            // Replaces the previous `logMessageThunk` dispatch with a direct
            // backendClient call, preserving the Redux side-effects inline.
            const logResponse = await backendClient.deleteUserMessageAndCreateLog({
                messageId: userMessage._id,
                waMessageKey: hit.waMessageKey,
                // Carry the ack we gated on, so the terminal row records the evidence
                // rather than just the verdict.
                ack: storeAck,
                sendFailureError: undefined,
                attemptType,
                whatsappStatus,
                logMessage,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
                ...storeCheckMetadata,
            });
            if ((logResponse as any)?.message) {
                dispatch(userMessageReducerActions.updateOne({
                    id: userMessage._id,
                    changes: (logResponse as any).message,
                }));
            }
            if ((logResponse as any)?.isRecurringScheduled && (logResponse as any)?.newMessage) {
                dispatch(userMessageReducerActions.addOne((logResponse as any).newMessage));
            }

            return; // Exit early - message was already sent
        }

        // Message not found in store - log and proceed with retry
        console.log(`[retry send] GATE 1 PASSED: Message not found in store - proceeding with retry`);
        console.log(`[MANUAL_RETRY_DEBUG] Message not found in store - proceeding with retry - messageId: ${userMessage._id}`);
        const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
        const clientType = getClientType();
        await backendClient.createMessageAttemptLog({
            messageId: userMessage._id,
            attemptType,
            whatsappStatus,
            // Distinguish a genuine miss from an un-acked hit — they are different
            // findings and the previous wording collapsed them into one.
            logMessage: waMessageKey
                ? `Message found in store but not acked (ack ${storeAck}) - ${attemptText} will proceed${debugInfoSummary}`
                : `Message not found in store - ${attemptText} will proceed${debugInfoSummary}`,
            retryTrigger,
            mqttClientId: mqttClientId || undefined,
            clientType,
            waVersion,
            waMeUser,
            waState,
            waStream,
            waHasSynced,
            ...storeCheckMetadata,
        });
    } catch (storeCheckError) {
        console.error(`[MANUAL_RETRY_DEBUG] Error checking store for message - messageId: ${userMessage._id}:`, storeCheckError);

        // Create log for error checking store
        try {
            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage: `Error checking store for message - cannot safely retry (aborting)`,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
                error: storeCheckError instanceof Error ? storeCheckError.message : String(storeCheckError),
            });
        } catch (logError) {
            console.error(`[MANUAL_RETRY_DEBUG] Failed to create log for store check error:`, logError);
        }

        // If check fails, we CANNOT safely proceed - we don't know if message was already sent
        // Proceeding could cause duplicate sends, so we abort
        return;
    }

    // #449 — an automatic wake on a client that isn't connected (UNPAIRED / qrcode / logged out)
    // stops here: it can't send, so it must not touch the lock or the retry counter. Left alone, it
    // armed the lock below and then refused at "WhatsApp not connected", putting every other client
    // of the owner in a 180s backoff. A manual retry still runs so the person sees the refusal.
    if (!isWhatsAppConnected && retryTrigger !== 'manual') {
        console.warn(`[retry send] WhatsApp not connected (state=${waState ?? 'unknown'}) - automatic retry skipped, lock and retryCount untouched - messageId: ${userMessage._id}`);
        try {
            if (notConnectedLogThrottle.shouldLog(String(userMessage._id), { state: waState, stream: waStream, hasSynced: waHasSynced })) {
            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage: `Retry skipped - WhatsApp not connected (state ${waState ?? 'unknown'})`,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
            });
            }
        } catch (logError) {
            console.error(`[retry send] Failed to create not-connected skip log:`, logError);
        }
        return;
    }

    // #700 — same for a page that says connected while WhatsApp Web's socket is closed (the breaker is open):
    // without this every wake bumped retryCount on a send that could only abort, until the message failed.
    waConnectionBreaker.observeConnection(isWhatsAppConnected);
    if (retryTrigger !== 'manual' && waConnectionBreaker.decide(String(userMessage._id)) === 'blocked') {
        console.warn(`[retry send] #700 WhatsApp Web disconnected - automatic retry skipped, lock and retryCount untouched - messageId: ${userMessage._id}`);
        if (notConnectedLogThrottle.shouldLog(String(userMessage._id), { state: 'breaker-open', stream: waStream, hasSynced: waHasSynced })) await backendClient.createMessageAttemptLog({
            messageId: userMessage._id,
            attemptType,
            whatsappStatus: 'disconnected',
            logMessage: waDisconnectedDeferLog('Retry skipped - WhatsApp not connected', waConnectionBreaker.openForMs()),
            retryTrigger,
            waState,
            waStream,
            waHasSynced,
        }).catch(() => undefined);
        return;
    }

    // Initial attempt hasn't been made: log it and send as INITIAL instead of retrying.
    // Reset retryCount=0 so the recursed flow is logged as attemptType='initial'.
    const recoverAsInitialSend = async () => {
        try {
            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage: `Retry requested but initial attempt lock doesn't exist - triggering initial send instead`,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
            });
        } catch (logError) {
            console.error(`[MANUAL_RETRY_DEBUG] Failed to create log for initial attempt lock check:`, logError);
        }
        await dispatch(sendScheduleMessageThunk({
            userMessage: { ...userMessage, retryCount: 0 },
            isSendMessageNow: false,
            // FE#360 — carry the human-initiated signal across this boundary,
            // or a user's manual Status retry is refused by the ledger.
            userInitiated: retryTrigger === 'manual',
        }));
    };

    // Check if initial attempt lock exists before allowing retry
    // If initial attempt hasn't been made, trigger initial send instead of retry
    console.log(`[retry send] GATE 2: Checking if initial attempt lock exists`);
    // #449 — peek READ-ONLY first. The attempt-0 probe below GRANTS when no lock exists: it arms
    // `:sent`/`:lastAt`, and the initial send that follows is then denied `backoff` by that same
    // grant (180s self-denial per message). With a lock present the probe denies without
    // refreshing `:lastAt`, so it is safe to keep for its #446 verdict. A failed peek falls
    // through to the probe, as before.
    try {
        const lockPeek = await withTimeout(
            checkInitialAttemptLockExists(userMessage._id),
            'checkInitialAttemptLockExists-gate2',
            timeoutMs
        );
        if (lockPeek && lockPeek.initialAttemptLockExists === false) {
            console.warn(`[retry send] GATE 2: Initial attempt lock doesn't exist (read-only check) - triggering initial send instead of retry - messageId: ${userMessage._id}`);
            await recoverAsInitialSend();
            return;
        }
    } catch (peekError) {
        console.warn(`[retry send] GATE 2: read-only lock check failed - falling back to the server verdict - messageId: ${userMessage._id}`, peekError);
    }
    // #446 — the server's lock verdict; the retry budget below is capped on its grant count.
    let serverLock: ServerLockVerdict | undefined;
    try {
        console.log(`[MANUAL_RETRY_DEBUG] Checking if initial attempt lock exists - messageId: ${userMessage._id}`);
        const initialAttemptCheck = await withTimeout(
            backendClient.hasSendAttemptBeenMadeToMessage({
                messageId: userMessage._id,
                retryAttemptNumber: 0,
                isSendMessageNow: false,
            }),
            'hasSendAttemptBeenMadeToMessage-initial-check',
            timeoutMs
        );

        console.log(`[retry send] GATE 2 result: initialAttemptLockExists = ${initialAttemptCheck?.initialAttemptLockExists}`);

        // BackendActionsClient swallows transport errors and returns undefined.
        // Treat undefined as "couldn't determine lock state" → fail-safe path
        // (write log + proceed with retry, matching the previous catch branch).
        const concluded = serverDocConcluded(initialAttemptCheck?.userMessage);
        if (initialAttemptCheck === undefined) {
            console.warn(`[MANUAL_RETRY_DEBUG] Initial attempt lock check returned undefined (transport error swallowed) - proceeding (fail-safe) - messageId: ${userMessage._id}`);
            try {
                const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                const clientType = getClientType();
                await backendClient.createMessageAttemptLog({
                    messageId: userMessage._id,
                    attemptType,
                    whatsappStatus,
                    logMessage: `Error checking initial attempt lock - proceeding with retry (fail-safe)`,
                    retryTrigger,
                    mqttClientId: mqttClientId || undefined,
                    clientType,
                    waVersion,
                    waMeUser,
                    waState,
                    waStream,
                    waHasSynced,
                });
            } catch (logError) {
                console.error(`[MANUAL_RETRY_DEBUG] Failed to create fail-safe log for undefined lock-check:`, logError);
            }
            // Fall through to retry logic (fail-safe).
        } else if (concluded.concluded) {
            // #446 — the server's doc proves the send concluded (delivery key / confirmed / deleted), so
            // it denies every further attempt as `already_terminal`. Counting this wake as a retry is
            // what flipped keyed `sent_pending_ack` messages to `failed` at the cap. Stop here without
            // touching the doc's failure fields; ack/delivery stays with the reconciler and reaper.
            const { reason, status: serverStatus } = concluded;
            console.log(`[retry send] GATE 2: server reports send concluded (${reason}, status ${serverStatus ?? 'n/a'}) - retry skipped, retryCount unchanged - messageId: ${userMessage._id}`);
            try {
                const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                const clientType = getClientType();
                await backendClient.createMessageAttemptLog({
                    messageId: userMessage._id,
                    attemptType,
                    whatsappStatus,
                    logMessage: `Retry skipped - server reports the send concluded (${reason}, status ${serverStatus ?? 'n/a'})`,
                    retryTrigger,
                    mqttClientId: mqttClientId || undefined,
                    clientType,
                    waVersion,
                    waMeUser,
                    waState,
                    waStream,
                    waHasSynced,
                });
            } catch (logError) {
                console.error(`[retry send] Failed to create concluded-skip log:`, logError);
            }
            // Bring the local copy in line so the pacer (status === 'pending') stops offering it.
            if (reason === 'deleted') {
                dispatch(userMessageReducerActions.removeOne(userMessage._id));
            } else {
                const parsedStatus = MessageStatusSchema.safeParse(serverStatus);
                if (parsedStatus.success) {
                    dispatch(userMessageReducerActions.updateOne({ id: userMessage._id, changes: { status: parsedStatus.data } }));
                }
            }
            return;
        } else if (!initialAttemptCheck.initialAttemptLockExists) {
            console.warn(`[retry send] GATE 2: Initial attempt lock doesn't exist - triggering initial send instead of retry`);
            console.warn(`[MANUAL_RETRY_DEBUG] Initial attempt lock doesn't exist - triggering initial send instead of retry - messageId: ${userMessage._id}`);
            // Reached only if the lock vanished between the peek and this probe (or the peek failed).
            await recoverAsInitialSend();
            return;
        }

        serverLock = initialAttemptCheck;
        console.log(`[retry send] GATE 2 PASSED: Initial attempt lock exists - proceeding with retry`);
        console.log(`[MANUAL_RETRY_DEBUG] Initial attempt lock exists - proceeding with retry - messageId: ${userMessage._id}`);
    } catch (checkError) {
        console.error(`[MANUAL_RETRY_DEBUG] Error checking initial attempt lock - messageId: ${userMessage._id}:`, checkError);

        // Create log for error checking initial attempt lock
        try {
            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();
            await backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage: `Error checking initial attempt lock - proceeding with retry (fail-safe)`,
                retryTrigger,
                mqttClientId: mqttClientId || undefined,
                clientType,
                waVersion,
                waMeUser,
                waState,
                waStream,
                waHasSynced,
            });
        } catch (logError) {
            console.error(`[MANUAL_RETRY_DEBUG] Failed to create log for initial attempt lock check error:`, logError);
        }

        // If check fails, proceed with retry logic (fail-safe)
    }

    let sendMessageViaWhatsappResponse: any = null;

    try {
        // Simple retry condition: retry if enabled AND (haven't exhausted retries OR it's a manual retry)
        const isRetryEnabled = settings?.isSendMessageRetryEnabled === true;
        const currentRetryCount = userMessage.retryCount ?? 0;
        const maxRetries = settings?.messageMaxRetries ?? 0;
        const isManualRetry = retryTrigger === 'manual';
        // #446 — budget on the server's grant count when GATE 2 gave one. The client counter also
        // counted wakes that were denied or never dispatched, and capped messages early.
        const budget = resolveRetryBudget({ isRetryEnabled, maxRetries, isManualRetry, clientRetryCount: currentRetryCount, serverLock });
        const shouldRetry = budget.shouldRetry;

        console.log(`[retry send] Retry decision calculation:`, {
            isRetryEnabled,
            currentRetryCount,
            maxRetries,
            isManualRetry,
            shouldRetry,
            budgetSource: budget.source,
            attemptsUsed: budget.attemptsUsed,
            calculation: `${isRetryEnabled} && (${budget.attemptsUsed} < ${maxRetries} || ${isManualRetry}) = ${shouldRetry}`
        });

        // Minimum spacing between send attempts. Kept STRICTLY BELOW the server's re-dispatch lock
        // (scheduler.ts lockExpiryTime, 3 min) so a scheduler-dispatched retry is never bounced here
        // — otherwise the two gates misalign and the effective retry interval doubles (~9-10 min),
        // starving the transient media-send retries before the 30-min reaper window.
        const MIN_RETRY_GAP_MINUTES = 2;
        console.log(`[retry send] Checking lastSendAttempt timing - lastSendAttempt: ${userMessage.lastSendAttempt}`);
        // FE#242: a MANUAL retry bypasses the inter-send gap, exactly as it bypasses
        // the 30-minute expiration gate above. The gap only exists to pace the
        // AUTOMATIC (mqtt/fcm/scheduler) retry loop so it can't stampede the engine.
        // Once auto-resend-on-failure started stamping `lastSendAttempt` on every
        // attempt, this gate began rejecting the user's explicit Retry click (which
        // almost always lands within 2 min of the last automatic attempt) — the button
        // "did nothing". A user asking to send now must not be paced against a
        // background retry they never saw.
        if (userMessage.lastSendAttempt && retryTrigger === 'manual') {
            console.log(`[retry send] MANUAL retry - bypassing the ${MIN_RETRY_GAP_MINUTES}-minute inter-send gap (user explicitly requested send)`);
        }
        if (userMessage.lastSendAttempt && retryTrigger !== 'manual') {
            const minRetryGapMillis = MIN_RETRY_GAP_MINUTES * 60 * 1000;
            const timeSinceLastSend = now.getTime() - new Date(userMessage.lastSendAttempt).getTime();

            console.log(`[retry send] Time since last send: ${Math.round(timeSinceLastSend / 60000)} minutes (need ${MIN_RETRY_GAP_MINUTES} minutes)`);
            if (timeSinceLastSend < minRetryGapMillis) {
                const minutesElapsed = Math.round(timeSinceLastSend / 60000);
                console.log(`[retry send] REJECTED - only ${minutesElapsed} minutes since last send (need ${MIN_RETRY_GAP_MINUTES} minutes). Returning early.`);
                console.log(`[MANUAL_RETRY_DEBUG] Retry rejected - only ${minutesElapsed} minutes since last send (need ${MIN_RETRY_GAP_MINUTES} minutes) - messageId: ${userMessage._id}`);

                // Create log for rejected retry due to timing
                try {
                    const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                    const clientType = getClientType();
                    await backendClient.createMessageAttemptLog({
                        messageId: userMessage._id,
                        attemptType,
                        whatsappStatus,
                        logMessage: `Retry rejected - only ${minutesElapsed} minutes since last send attempt (minimum ${MIN_RETRY_GAP_MINUTES} minutes required)`,
                        retryTrigger,
                        mqttClientId: mqttClientId || undefined,
                        clientType,
                    });
                } catch (logError) {
                    console.error("[retrySendMessageThunk] Failed to create timing rejection log:", logError);
                }

                return; // Exit early - retry not allowed yet
            }
            console.log(`[retry send] Timing check PASSED - elapsed ${Math.round(timeSinceLastSend / 60000)} minutes >= ${MIN_RETRY_GAP_MINUTES} minutes`);
        } else {
            console.log(`[retry send] No lastSendAttempt found - timing check skipped`);
        }

        if (shouldRetry) {
            console.log(`[retry send] shouldRetry=true - proceeding with retry flow`);
            // Check if initial attempt lock exists before incrementing retry counter
            // Only increment if initial attempt was made (lock exists)
            try {
                console.log(`[retry send] Checking initial attempt lock before incrementing retryCount`);
                console.log(`[MANUAL_RETRY_DEBUG] Checking initial attempt lock before incrementing retryCount - messageId: ${userMessage._id}`);
                const initialLockCheck = await withTimeout(
                    checkInitialAttemptLockExists(userMessage._id),
                    'checkInitialAttemptLockExists',
                    timeoutMs
                );

                if (!initialLockCheck?.initialAttemptLockExists) {
                    console.warn(`[MANUAL_RETRY_DEBUG] Initial attempt lock doesn't exist - cannot increment retryCount. Triggering initial send instead - messageId: ${userMessage._id}`);

                    // Initial attempt hasn't been made, trigger initial send instead of incrementing retry.
                    // Reset retryCount=0 so the recursed flow is logged as attemptType='initial'.
                    await dispatch(sendScheduleMessageThunk({
                        userMessage: { ...userMessage, retryCount: 0 },
                        isSendMessageNow: false,
                        userInitiated: retryTrigger === 'manual', // FE#360
                    }));
                    return;
                }

                console.log(`[MANUAL_RETRY_DEBUG] Initial attempt lock exists - safe to increment retryCount - messageId: ${userMessage._id}`);
            } catch (checkError) {
                console.error(`[MANUAL_RETRY_DEBUG] Error checking initial attempt lock before incrementing retryCount - messageId: ${userMessage._id}:`, checkError);
                // #552: this catch used to be console-only, so a probe that threw (and a retry that went ahead
                // fail-safe) left no row. Best-effort trace row; the reaper reads the prefix as `lock_error`.
                try {
                    await backendClient.createMessageAttemptLog({
                        messageId: userMessage._id,
                        attemptType,
                        whatsappStatus,
                        logMessage: `Error checking initial attempt lock before incrementing retryCount - proceeding with retry (fail-safe): ${describeError(checkError)}`,
                        retryTrigger,
                        mqttClientId: MQTTSubscriber.getInstance().getCurrentClientId() || undefined,
                        clientType: getClientType(),
                        waVersion,
                        waMeUser,
                        waState,
                        waStream,
                        waHasSynced,
                    });
                } catch (logError) {
                    console.error(`[retry send] Failed to create lock-probe error log - messageId: ${userMessage._id}:`, logError);
                }
                // If check fails, proceed with retry logic (fail-safe)
            }

            // #446 — one past the attempts really used (server count when known), never an inflated count.
            const nextRetryCount = budget.nextRetryCount;

            // Patch the message to update retryCount (lastSendAttempt will be set after actual send)
            await dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: userMessage._id,
                    retryCount: nextRetryCount
                }
            }));

            // Build updated message in memory. (Previously merged `patchResult.payload`
            // which was the action-object `{entity:...}`, not the entity — leaving
            // retryCount unchanged. Trust the in-memory increment.)
            const updatedUserMessage: UserMessage = { ...userMessage, retryCount: nextRetryCount };

            console.log(`[retry send] Patched retryCount to ${nextRetryCount}. Now dispatching sendMessageThunk...`);
            console.log(`[retry send] Updated message for sendMessageThunk:`, JSON.stringify({
                _id: updatedUserMessage._id,
                status: updatedUserMessage.status,
                retryCount: updatedUserMessage.retryCount
            }));

            if (retryTrigger === 'mqtt' || retryTrigger === 'fcm') {
                // For mqtt/fcm triggers: set status back to 'pending' so the
                // schedule-service loop (which already owns pacing/selection) picks
                // it up on the next alarm tick via orderPendingForSend. Avoids
                // a concurrent direct-dispatch from inside the retry thunk while
                // the pacer may also be running.
                console.log(`[PACER] retry ENQUEUE id=${userMessage._id} trigger=${retryTrigger} rc->${nextRetryCount} (re-pending, pacer will drain)`);
                dispatch(userMessageReducerActions.updateOne({
                    id: userMessage._id,
                    changes: { status: 'pending', retryCount: nextRetryCount },
                }));
                // #534: the hand-off to the pacer wrote no row, so a retry the pacer never drained read as
                // "store miss, then silence" at reap time. Best-effort — never blocks the enqueue.
                try {
                    await backendClient.createMessageAttemptLog({
                        messageId: userMessage._id,
                        attemptType,
                        whatsappStatus,
                        logMessage: `Retry #${nextRetryCount} queued for the send pacer (trigger=${retryTrigger})`,
                        retryTrigger,
                        mqttClientId: MQTTSubscriber.getInstance().getCurrentClientId() || undefined,
                        clientType: getClientType(),
                        waVersion,
                        waMeUser,
                        waState,
                        waStream,
                        waHasSynced,
                    });
                } catch (logError) {
                    console.error(`[retry send] Failed to create retry-queued log - messageId: ${userMessage._id}:`, logError);
                }
            } else {
                // For manual retries: dispatch directly so the user sees an
                // immediate response rather than waiting for the next alarm.
                console.log(`[PACER] retry DIRECT id=${userMessage._id} trigger=${retryTrigger} (manual -> immediate send)`);
                dispatch(sendScheduleMessageThunk({
                    userMessage: updatedUserMessage as UserMessage,
                    userInitiated: retryTrigger === 'manual', // FE#360
                }));
            }
            console.log(`[retry send] sendMessageThunk dispatch completed`);
        } else {
            console.log(`[retry send] shouldRetry=false - NOT retrying`);
            // Retry not accepted
            const maxRetriesExhausted = budget.exhausted;

            // A keyed server doc never reaches here (GATE 2 stops it above), so a server-counted cap is
            // an unkeyed doc the server has also failed; writing the #408 cause on it is consistent.
            if (maxRetriesExhausted && hasDeliveryKey(userMessage.waMessageKey)) {
                // #446 — this copy carries a delivery key (an attempt reached WhatsApp) and no server doc
                // said otherwise. Never flip it to `failed` here; ack/delivery is the reconciler's and reaper's call.
                console.log(`[retry send] retry budget exhausted but message is keyed - not failing it - messageId: ${userMessage._id}`);
            } else if (maxRetriesExhausted) {
                // #408 — keep WHY the attempts failed instead of only the cap text.
                const capFailure = resolveRetryCapFailure({
                    currentRetryCount: budget.attemptsUsed,
                    maxRetries,
                    lastStoreCheckAck,
                    prior: { sendFailureReason: userMessage.sendFailureReason, sendFailureError: userMessage.sendFailureError },
                });
                const failureReason = capFailure.sendFailureError;

                // Patch message status to failed
                dispatch(DBUserMessageThunks.patch({
                    entity: {
                        _id: userMessage._id,
                        status: "failed",
                        ...capFailure,
                    }
                }));

                // Create failed log
                try {
                    const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                    const clientType = getClientType();
                    await createFailedMessageLog(
                        userMessage._id,
                        failureReason,
                        attemptType,
                        'failed',
                        retryTrigger,
                        mqttClientId || undefined,
                        clientType,
                        waVersion,
                        waMeUser,
                        undefined,
                        undefined,
                        waState,
                        waStream,
                        waHasSynced
                    );
                } catch (logError) {
                    console.error("[retrySendMessageThunk] Failed to create max retries failure log:", logError);
                }
            } else {
                // Other rejection reasons (e.g. settings disabled) - mark as failed
                const rejectionReason = !isRetryEnabled
                    ? 'Retry is disabled in settings'
                    : 'Unknown rejection reason';

                const failureReason = `Retry not accepted. Reason: ${rejectionReason}. Params: retryEnabled=${isRetryEnabled}, currentRetryCount=${currentRetryCount}, maxRetries=${maxRetries}`;

                // Patch message status to failed
                dispatch(DBUserMessageThunks.patch({
                    entity: {
                        _id: userMessage._id,
                        status: "failed",
                        sendFailureError: failureReason
                    }
                }));

                try {
                    const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                    const clientType = getClientType();
                    await createFailedMessageLog(
                        userMessage._id,
                        failureReason,
                        attemptType,
                        'failed',
                        retryTrigger,
                        mqttClientId || undefined,
                        clientType,
                        waVersion,
                        waMeUser,
                        undefined,
                        undefined,
                        waState,
                        waStream,
                        waHasSynced
                    );
                } catch (logError) {
                    console.error("[retrySendMessageThunk] Failed to create retry rejection log:", logError);
                }
            }
        }
    } catch (error) {
        const errAny = error as { response?: { data?: { message?: string } } } | null | undefined;
        if (errAny?.response?.data?.message?.includes("No record found for id")) {
            console.warn(`[sendMessageThunk] Message ${userMessage._id} not found, removing it from the store`);
            dispatch(userMessageReducerActions.removeOne(userMessage._id));
        }
        else {
            console.error(`[retry send] Error in retrySendMessageThunk:`, error);
            console.error(`Error in retrySendMessageThunk:`, error);
            // #534: this used to end on the console, so the message's logs just stopped and the reaper
            // could not say why. #552: written as a TRACE row (attempt log), not a 'failed' one: no send was
            // attempted here, a failed row would flip the reaper's stage to attempt_failed and count in log-based
            // campaign stats, and the message's status is not touched.
            try {
                await backendClient.createMessageAttemptLog({
                    messageId: userMessage._id,
                    attemptType,
                    whatsappStatus,
                    logMessage: retryThunkErrorReason(error),
                    retryTrigger,
                    mqttClientId: MQTTSubscriber.getInstance().getCurrentClientId() || undefined,
                    clientType: getClientType(),
                    waVersion,
                    waMeUser,
                    waState,
                    waStream,
                    waHasSynced,
                });
            } catch (logError) {
                console.error("[retrySendMessageThunk] Failed to create retry-thunk error log:", logError);
            }
        }
    }

    console.log(`[retry send] retrySendMessageThunk EXITING - messageId: ${userMessage._id}, sendMessageViaWhatsappResponse:`, sendMessageViaWhatsappResponse);
    return sendMessageViaWhatsappResponse;
    } finally {
        clearSendLogMetadata(String(userMessage._id));
    }
});


export const sendViaWaAndLogMessageThunk = createAsyncThunk("sendViaWhatsappAndLogThunk",
    async ({ preparedWaMessage, userMessage, shouldSendMessage = true, attemptType, waVersion, waMeUser }:
        { preparedWaMessage: WaMessage; userMessage: UserMessage, shouldSendMessage?: boolean; attemptType?: 'initial' | 'client-retry' | 'scheduler-retry'; waVersion?: string; waMeUser?: string; },
        { dispatch }) => {

        if (DISABLE_SEND_MESSAGE) {
            console.warn("[sendViaWaAndLogMessageThunk] DISABLE_SEND_MESSAGE is true");
            return;
        }

        let sendMessageViaWhatsappResponse: any = null;
        let waMessageKey: any = null;

        console.log(`[sendMessageThunk] [userMessage id]: ${userMessage?._id} [sendMessageViaWhatsappResponse]: ${waMessageKey}`);
        try {
            console.log(`[sendMessageThunk] [shouldSendMessage] ${shouldSendMessage} [userMessage id]: ${userMessage?._id} `);

            if (shouldSendMessage) {
                const sendToolResult = await sendMessageThunkTool({
                    userMessage,
                });
                const sendToolResultAny: any = sendToolResult;
                // sendToolResult is the canonical WaToolResponse<WaSendMessageThunkResult>
                // envelope. The inner payload (with id/messageId/waMessageKey/response/
                // waMeInfo) lives under `.data`. The legacy code path read
                // top-level fields on a flatter shape — fan out via `?? data ?? envelope`
                // so any future flatter emit still works.
                const sendData: any = sendToolResultAny?.data ?? sendToolResultAny;
                sendMessageViaWhatsappResponse = sendData?.response ?? sendData;

                // Check if the response contains an error (either as error property or error object)
                const responseError = sendToolResultAny?.success === false
                    ? sendToolResultAny?.error
                    : sendMessageViaWhatsappResponse?.error;
                const hasError = responseError !== undefined && responseError !== null;

                if (hasError) {
                    // Extract error message
                    const errorMessage = typeof responseError === 'string'
                        ? responseError
                        : responseError?.message || responseError?.toString() || 'Unknown send error';

                    console.error(`[sendViaWaAndLogMessageThunk] Send failed with error: ${errorMessage}`);

                    // Update message with failure error. The canonical failure
                    // log row is written by runSendProtocol /
                    // deleteUserMessageAndCreateLog (see plan 5f Task 12) — do
                    // not emit a duplicate logMessageThunk here.
                    // sendFailureError is CUSTOMER-VISIBLE (rendered verbatim,
                    // exposed via /v1) — strip diagnostic suffixes.
                    dispatch(DBUserMessageThunks.patch({
                        entity: {
                            _id: userMessage._id,
                            sendFailureError: stripDiagnosticSuffixes(errorMessage)
                        }
                    }));

                    // Set lastSendAttempt even when send fails with error response
                    const lastSendAttempt = new Date();
                    dispatch(DBUserMessageThunks.patch({
                        entity: {
                            _id: userMessage._id,
                            lastSendAttempt: lastSendAttempt
                        }
                    }));

                    // Return early - don't proceed with success handling. The
                    // taxonomy code rides along so sendFn can tell a deterministic
                    // page failure from an ambiguous one (QA 3b E7).
                    return { error: errorMessage, code: typeof responseError === 'object' ? responseError?.code : undefined };
                }

                waMessageKey =
                    sendData?.waMessageKey ??
                    sendData?.id ??
                    sendData?.messageId ??
                    sendMessageViaWhatsappResponse?._serialized ??
                    sendMessageViaWhatsappResponse?.id?._serialized ??
                    sendMessageViaWhatsappResponse?.id ??
                    "";

                // If we got a response but no message key, this might also be an error
                if (!waMessageKey && sendMessageViaWhatsappResponse) {
                    console.warn(`[sendViaWaAndLogMessageThunk] Send response received but no waMessageKey extracted. Response:`, sendMessageViaWhatsappResponse);
                }
            }

            // Immediately update Redux state with waMessageKey for real-time matching
            if (waMessageKey) {
                console.log(`🔧 [RealTimeMessageAckHandler] Updating Redux with waMessageKey for message ${userMessage._id}:`, waMessageKey);
                dispatch(userMessageReducerActions.updateOne({
                    id: userMessage._id,
                    changes: { waMessageKey }
                }));
            }

            // Set lastSendAttempt after actual send attempt (success or failure)
            const lastSendAttempt = new Date();
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: userMessage._id,
                    lastSendAttempt: lastSendAttempt
                }
            }));

            // The canonical success log row is written by runSendProtocol /
            // deleteUserMessageAndCreateLog (see plan 5f Task 12) — do not
            // emit a duplicate logMessageThunk here. Connection-status fetch
            // is still needed below for the investigation-fallback log path.
            const connectionStatus = await ContentMessageServiceActions.isWhatsAppConnectedThunkTool();
            const { connected: isWhatsAppConnected, state: waState, stream: waStream, has_synced: waHasSynced } = connectionStatus;
            const finalWhatsappStatus = isWhatsAppConnected ? 'connected' : 'disconnected';

            const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
            const clientType = getClientType();

            // If no waMessageKey was returned, investigate by checking the store
            if (!waMessageKey && shouldSendMessage) {
                try {
                    console.log(`[sendViaWaAndLogMessageThunk] No waMessageKey returned, investigating store for message ${userMessage._id}`);

                    const investigationDueDate = preparedWaMessage.dueDate
                        ? new Date(preparedWaMessage.dueDate)
                        : new Date(new Date().getTime() - 20 * 60 * 1000); // 20 minutes before now if no dueDate

                    const investigationResult = await withTimeout(
                        ContentMessageServiceActions.getUnloggedWaMessageKeyIfExistInStore(
                            preparedWaMessage.chatId ?? '',
                            userMessage._id,
                            investigationDueDate
                        ),
                        'getUnloggedWaMessageKeyIfExistInStore-investigation',
                        timeoutMs
                    );

                    const investigationOk = investigationResult && !isWireError(investigationResult) ? investigationResult : undefined;
                    const foundWaMessageKey = investigationOk?.waMessageKey || null;
                    const debugInfo = investigationOk?.debugInfo;

                    // Log debug info to console
                    if (debugInfo) {
                        console.log(`[sendViaWaAndLogMessageThunk] Investigation Debug Info:`);
                        console.log(`  - Total messages fetched: ${debugInfo.totalMessagesFetched}`);
                        console.log(`  - Top 5 messages:`);
                        debugInfo.top5Messages.forEach((msg, index) => {
                            console.log(`    ${index + 1}. ID: ${msg.id}`);
                            console.log(`       ack: ${msg.ack ?? 'n/a'}, fromMe: ${msg.fromMe ?? 'n/a'}`);
                        });
                    }

                    // Create separate log entry for investigation. This is a
                    // FORENSIC note, not a canonical send-result row — write
                    // via createMessageAttemptLog (records a log entry) NOT
                    // deleteUserMessageAndCreateLog (which would re-bump
                    // campaign.sentCount via mongoose hooks and re-trigger
                    // the next-campaign-message cascade — the canonical
                    // success log already did that).
                    const investigationLogMessage = foundWaMessageKey
                        ? `Investigation: Message key found in store after send (checked ${debugInfo?.totalMessagesFetched || 0} messages)`
                        : `Investigation: Message key not found in store (checked ${debugInfo?.totalMessagesFetched || 0} messages)`;

                    await createMessageAttemptLog(
                        userMessage._id,
                        attemptType ?? 'initial',
                        finalWhatsappStatus,
                        investigationLogMessage,
                        undefined,
                        mqttClientId || undefined,
                        clientType,
                        waVersion,
                        waMeUser,
                        debugInfo,
                    );

                    // If we found the key, update Redux state
                    if (foundWaMessageKey) {
                        console.log(`🔧 [sendViaWaAndLogMessageThunk] Updating Redux with found waMessageKey for message ${userMessage._id}:`, foundWaMessageKey);
                        dispatch(userMessageReducerActions.updateOne({
                            id: userMessage._id,
                            changes: { waMessageKey: foundWaMessageKey }
                        }));
                    }
                } catch (investigationError) {
                    console.error(`[sendViaWaAndLogMessageThunk] Error during investigation:`, investigationError);

                    // Log the investigation failure for completeness
                    const connectionStatus = await ContentMessageServiceActions.isWhatsAppConnectedThunkTool();
                    const { connected: isWhatsAppConnected, state: waState, stream: waStream, has_synced: waHasSynced } = connectionStatus;
                    const finalWhatsappStatus = isWhatsAppConnected ? 'connected' : 'disconnected';
                    const mqttClientId = MQTTSubscriber.getInstance().getCurrentClientId();
                    const clientType = getClientType();

                    // Forensic note — use createMessageAttemptLog for the
                    // same reason as the investigation-success branch above.
                    await createMessageAttemptLog(
                        userMessage._id,
                        attemptType ?? 'initial',
                        finalWhatsappStatus,
                        `Investigation: Failed to check store for message key - ${(investigationError as { message?: string } | null)?.message || 'Unknown error'}`,
                        undefined,
                        mqttClientId || undefined,
                        clientType,
                        waVersion,
                        waMeUser,
                        undefined,
                        (investigationError as { message?: string } | null)?.message || String(investigationError),
                        waState,
                        waStream,
                        waHasSynced,
                    );

                    // Don't throw - investigation failure shouldn't break the flow
                }
            }
        } catch (error) {
            console.error(`Error in sendViaWhatsappAndLogThunk:`, error);

            // Update message with failure error. The canonical exception log
            // row is written by runSendProtocol / deleteUserMessageAndCreateLog
            // (see plan 5f Task 12) — do not emit a duplicate logMessageThunk
            // here. We re-throw so the orchestrator's sendFn wrapper sees the
            // failure and writes the canonical row.
            const errMsg = error instanceof Error ? (error.message || error.toString()) : String(error);
            // sendFailureError is CUSTOMER-VISIBLE — strip diagnostic suffixes.
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: userMessage._id,
                    sendFailureError: stripDiagnosticSuffixes(errMsg)
                }
            }));

            // Set lastSendAttempt even on error (send attempt was made)
            const lastSendAttempt = new Date();
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: userMessage._id,
                    lastSendAttempt: lastSendAttempt
                }
            }));
        }

        return sendMessageViaWhatsappResponse;
    }
);


// logMessageThunk was removed in plan-5f cleanup. Canonical send-result
// rows are written by bt-common's runSendProtocol (which calls
// deleteUserMessageAndCreateLog directly). Forensic / investigation rows
// are written by createMessageAttemptLog (which doesn't trigger the
// delete-cascade). No remaining caller needed the legacy thunk's
// "log + recurring-message dispatch" wrapper — the orchestrator's
// post-send handler does the same Redux dispatches inline.

export const nextCampaignMessageThunk = createAsyncThunk<
    void,
    string,
    { state: CommonRootState }
>("nextCampaignMessageThunk", async (campaignId, thunkApi) => {

    if (DISABLE_SEND_MESSAGE) {
        console.warn("[nextCampaignMessageThunk] DISABLE_SEND_MESSAGE is true");
        return;
    }

    const { dispatch } = thunkApi;
    const timeoutMs = 30000; // Set your timeout duration (in ms)

    console.log(`[nextCampaignMessageThunk] Starting next campaign message for campaignId: ${campaignId}`);

    try {
        let res = await withTimeout(getNextCampaignMessage(campaignId), 'getNextCampaignMessage', timeoutMs);
        if (res.campaign) {

            dispatch(campaignReducerActions.updateOne({
                id: campaignId,
                changes: res.campaign,
            }));
        }

        if (res.campaignMessages?.length > 0) {
            dispatch(userMessageReducerActions.upsertMany(res.campaignMessages));
        }

    } catch (error) {
        console.error(`[nextCampaignMessageThunk] Error in nextCampaignMessageThunk:`, error);
    }
});
